import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ArrowLeft, Info, Clock, GripVertical, CheckCircle2, MoreHorizontal, Trash2, Wand2, CircleDashed, Download, RefreshCw, Gauge } from 'lucide-react';
// Arrastre de la galería (tanda 3b, ajuste 6): dnd-kit (@dnd-kit/core +
// @dnd-kit/sortable, ~12 KB gz, sin dependencias transitivas). No había ninguna
// librería de arrastre en el proyecto; se eligió por peso y por integrarse con
// el estado de React (la foto arrastrada sigue el cursor semitransparente y las
// demás se desplazan animadas para abrir el hueco).
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS as DndCSS } from '@dnd-kit/utilities';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import { propsSoloLectura } from '../utils/soloLectura';
import DialogoDecision from '../components/ui/DialogoDecision';
import { useToast } from '../components/ToastManager';
import {
  emptyForm, productToForm, draftToForm, composeDescription, draftEsOfrecible,
} from '../utils/productDraft';
import { confirmacionDeEliminar } from '../utils/eliminarProducto';
import { listDrafts, saveDraft, deleteDraft } from '../utils/productDraftApi';
import {
  saveProductToWoo, saveVariationImage, updateMediaAlt, deleteProductToWoo,
  saveVariation, saveVariationsBatch, deleteVariation, createVariation,
  reportarFalloSubida, listarMarcas, listarColecciones, listarEjes, descargarFotosProducto, fotosUsos, optimizarFoto, historialOptimizacionProducto, registrarOptimizacionFallo, revertirOptimizacion, borrarOriginalesOptimizacion, completarTextosAlternativos,
  validarConsumible, crearConsumible, actualizarConsumible, capacidadConsumible, leerConsumible,
} from '../utils/productApi';
import SeccionComestible from '../components/SeccionComestible';
import { emptyComestible, comestibleAModelo, comestibleDesdeModelo } from '../utils/comestibleDraft';
import { masOpcionesDesdeFull, masOpcionesDeTipo, payloadOfertaVariacion, aplicarOrdenEjes, VISIBILIDADES, RESERVAS } from '../utils/masOpciones';
import { validarComestibleBasico, traducirErroresMotor } from '../utils/comestibleValidacion';
import { reunirMediaProducto, resumirPreview, pesoLegible, resumenOptimizacion } from '../utils/fotosOptimizar';
import { medirLista, reprocesarParaSubir } from '../utils/optimizacionPreview';
import { normalizeFilename, generateAltImagen } from '../utils/productImages';
import { distintivoFoto, etiquetaFilaSinReproceso } from '../utils/fotoDistintivo';
import { esBajaResolucion } from '../utils/bajaResolucion';
import EstadoOptimizacionFoto from '../components/EstadoOptimizacionFoto';
import MarcaOptimizacion from '../components/MarcaOptimizacion';
import FotoVariacionCard from '../components/FotoVariacionCard';
import HistorialOptToggle from '../components/HistorialOptToggle';
import {
  generateVariationSku, filaAFormulario, planDeEscrituras, renombrarOpcion,
  opcionQuedaHuerfana, resumenPadreVariable, skuDuplicadoDeFila, skuPropuestoAlRenombrar,
  atributosParaAlta, aplicarRenombreEje, afectadasPorRenombre, renombreHaciaExistente,
  recalcularOpcionesPadre,
} from '../utils/variacionesEdit';
import AgregarVariacionModal from '../components/AgregarVariacionModal';
import { tiposConfigurados, tipoActivo, prefijoSkuPropio, aplicaContenidoDeTipo, propFotoDeTipo } from '../utils/tiposProducto';
import { guiaDeProducto, listarGuias } from '../utils/guiasApi';
import { ordenarFilasPorEje } from '../utils/ordenFilasVariacion';
import { reordenarLista } from '../utils/reordenarLista';
import { colAhoraInputLocal, fmtFechaHoraBogota } from '../utils/colTimeUtils';
import { textoFrescura } from '../utils/frescuraTexto';
import { textoDeOrigen, tablaVacia } from '../utils/tablaMedidas';
import TablaMedidasEditor from '../components/TablaMedidasEditor';
import AutoTextarea from '../components/ui/AutoTextarea';
import ProductImageSelector from '../components/ProductImageSelector';
import CeldaFotoVariacion from '../components/CeldaFotoVariacion';
import { generateSku, skuExistsInCatalog, estadoWooLabel } from '../utils/productFormHelpers';
import { parseDescription, descriptionToText, textToHtml } from '../utils/productDescription';
import { esErrorDeSkuDuplicado } from '../utils/productSaveError';
import { requiredToSaveWoo } from '../utils/productWooPayload';
import { evaluarFicha, entradaDesdeForm } from '../utils/fichaEstado';
import { fusionarFormConSucios, fusionarVRowsConSucios, hayDescripcionSucia } from '../utils/productMergeSucios';
import { fotosDelProductoParaSelector, repartirFotosParaVariacion } from '../utils/fotosDeProducto';
import { normalizarEje, EJES_INICIALES } from '../utils/ejesVariacion';
import { esMultiEje, ejesDeVariacion } from '../utils/multiEje';
import { NORMALIZACION_NOMBRE_EJE_MULTIEJE } from '../utils/flagsCatalogo';
import FichaEstadoPanel from '../components/FichaEstadoPanel';
import ProductosRelacionados from '../components/ProductosRelacionados';
import {
  validateName, validateShortDescription, validateSku, validatePrice,
  validateSalePrice, validateWeight, validateDimensions, validateGtin,
} from '../utils/productValidation';
import './ProductPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const AUTOSAVE_MS = 12_000; // intervalo de autoguardado (§3.4)
// Tanda 3g: los campos de texto bajo la regla "solo viaja lo tocado" en el PUT.
// Tanda 3h: + productKind (el Tipo de negocio persiste como _vitrina_arquetipo;
// el valor DEDUCIDO de las categorías es sugerido y no viaja sin tocar).
const CAMPOS_TEXTO_TOCABLES = ['sku', 'gtin', 'description', 'shortDescription', 'seoTitle', 'seoMetadesc', 'productKind',
  // Tanda 4: contenido del padre (meta _vitrina_contenido)
  'contenidoValor', 'contenidoUnidad',
  // Tanda 5: guía de tallas — _vitrina_guia_id y _vitrina_medidas (solo tocadas)
  'guiaId', 'medidas',
  // Tanda 7: "Combínalo con" (cross_sell_ids) — misma regla de solo tocados
  'crossSellIds',
  // P3-4: marca (brandId) y colecciones (collectionTagIds) — solo tocadas viajan.
  'brandId', 'collectionTagIds',
  // P16 (Programados): el estado/programación. El `status` SOLO viaja si está
  // acá marcado (el usuario cambió el estado o la fecha de programación); un
  // guardado normal no lo manda y un producto programado sigue programado.
  'estado',
  // P3-12 «Más opciones»: cada campo viaja SOLO si el usuario lo tocó (misma regla del status).
  'featured', 'catalogVisibility', 'oferta', 'lowStockAmount', 'slug', 'defaultAttributes', 'backorders'];
// Tanda 4: campos editables de una FILA de variación (marca de tocado por fila).
// Tanda 4b: + gtin (columna Código de barras, ítem 9).
const CAMPOS_FILA_VARIACION = ['option', 'sku', 'regularPrice', 'salePrice', 'stockQty', 'contenidoValor', 'contenidoUnidad', 'gtin'];
// Tanda 4b (ítem 10): la ayuda del código de barras — una sola redacción para el
// campo del padre (simple) y el tooltip del encabezado de la columna (variable).
// Tanda 4c: la frase "no aplica" ya no nombra un tipo fijo — la agrega la ficha
// leyendo `aplicaGtin` del tipo de producto configurado.
const AYUDA_GTIN = 'Es el número de 13 dígitos que está debajo del código de barras del empaque. '
  + 'Google lo usa para identificar el producto exacto y mostrarlo en resultados de compras.';

function slugify(s) {
  return String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}
function hhmm(d) {
  try { return new Date(d).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
}
// P3-10 punto 1: etiqueta legible de un campo en conflicto de edición.
const ETIQUETAS_CONFLICTO = {
  name: 'Nombre', status: 'Estado', sku: 'SKU',
  regular_price: 'Precio', sale_price: 'Precio de oferta', stock_quantity: 'Stock',
};
function etiquetaCampoConflicto(campo) { return ETIQUETAS_CONFLICTO[campo] || campo; }
// Valor legible: el estado se traduce (publish → Publicado), el resto va tal cual
// (precio/sku/stock); vacío se muestra como «(sin valor)» para que no quede colgando.
function valorConflicto(campo, valor) {
  if (valor == null || valor === '') return '(sin valor)';
  if (campo === 'status') return estadoWooLabel(String(valor));
  return String(valor);
}
function varLabel(v) {
  const opts = (v.attributes || []).map((a) => a.option).filter(Boolean);
  return opts.join(' / ') || `Variación ${v.id}`;
}

// El textarea autoexpandible vive en components/ui/AutoTextarea desde la
// tanda 6 (parte 0.3): lo comparten la ficha y la nota de la tabla de medidas.

// Campo con etiqueta, ayuda, ejemplo y error en línea (nunca deshabilitado en silencio).
function Field({ label, required, help, example, error, children }) {
  return (
    <div className={`pp-field${error ? ' pp-field--err' : ''}`}>
      <label className="pp-label">{label}{required && <span className="pp-req"> *</span>}</label>
      {children}
      {help && <p className="pp-help">{help}</p>}
      {example && <p className="pp-example">{example}</p>}
      {error && <p className="pp-error">{error}</p>}
    </div>
  );
}

// Sección "llega en la tanda N" — no se oculta (§2).
function ComingSection({ title, nota }) {
  return (
    <div className="pp-coming">
      <h3>{title}</h3>
      <p><Info size={14} aria-hidden="true" /> {nota}</p>
    </div>
  );
}

// Tarjeta sortable de la galería (ajuste 6): dnd-kit maneja la posición; el
// arrastre se agarra por la FOTO (los botones y el alt siguen siendo clicables
// gracias al umbral de activación del sensor).
function SortableFoto({ img, idx, onHacerPrincipal, onQuitar, onAlt, nombreProducto = '', distintivo = null }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: img.id });
  const style = { transform: DndCSS.Transform.toString(transform), transition };
  // P3-7: alt con respaldo — una foto de producto sin texto alternativo cae al
  // nombre del producto, nunca a alt="" (vacío es para decorativas, no para esto).
  const altFoto = img.alt || nombreProducto || 'Foto del producto';
  return (
    <div
      ref={setNodeRef} style={style} {...attributes}
      className={`pp-gitem${idx === 0 ? ' pp-gitem--main' : ''}${isDragging ? ' pp-gitem--dragging' : ''}`}
    >
      {/* P3-9b: las etiquetas (Principal / distintivo) y las acciones viven SOBRE la
          imagen, en su propio contenedor — así nunca tapan el campo de texto
          alternativo (que queda abajo, siempre visible y editable). */}
      <div className="pp-gimg">
        <img src={img.src} alt={altFoto} {...listeners} />
        {idx === 0 && <span className="pp-gbadge">Principal</span>}
        {distintivo && <MarcaOptimizacion distintivo={distintivo} />}
        <div className="pp-gactions">
          {idx !== 0 && <button type="button" onClick={() => onHacerPrincipal(img.id)} title="Marcar como principal">★</button>}
          <button type="button" onClick={() => onQuitar(img.id)} title="Quitar del producto (no borra de la mediateca)">✕</button>
        </div>
      </div>
      <input className="pp-galt" value={img.alt || ''} placeholder="Texto alternativo"
        onChange={(e) => onAlt(img.id, e.target.value)} />
    </div>
  );
}

export default function ProductPage({ productId = null, categories = [], existingSkus = [], userRole, onBack, onSaved, initialDraft = null, onOpenProduct = null, buscarIdPorSku = null, storeName = '', catalogoConfig = null, plataforma = 'woo', onIrAGuias = null, productosCatalogo = [], focoInicial = null, soloLectura = false }) {
  const toast = useToast();
  const showToast = useMemo(() => (toast && toast.showToast) || (() => {}), [toast]);
  const isEdit = productId != null;

  // Tanda 4c — nada de la ficha asume la tienda ni sus tipos: los tipos de
  // producto y el prefijo de SKU de marca propia vienen de la configuración
  // (config.catalogo); sin configurar rige la semilla (utils/tiposProducto.js).
  const tipos = useMemo(() => tiposConfigurados(catalogoConfig), [catalogoConfig]);
  const tiposRef = useRef(tipos); tiposRef.current = tipos;
  const nombreTienda = String(storeName || '').trim() || 'tu tienda';
  const prefijoSku = prefijoSkuPropio(catalogoConfig, storeName);

  // initialDraft (tanda 3b, defecto 1c): un borrador local abierto desde el
  // listado retoma su contenido en modo nuevo.
  const [form, setForm] = useState(() => (initialDraft ? draftToForm(initialDraft) : emptyForm()));
  const [loading, setLoading] = useState(isEdit);
  const [tab, setTab] = useState('general');
  // P3-5b: sección Comestible (cuando el tipo tiene arquetipo «consumible»). El modelo
  // vive aparte del `form` base (que aporta nombre/descripción/imágenes); el Guardar lo
  // traduce y lo manda al motor del backend.
  const [comestible, setComestible] = useState(emptyComestible);
  const comestibleRef = useRef(comestible); comestibleRef.current = comestible;
  const [erroresComest, setErroresComest] = useState([]);
  const [capComest, setCapComest] = useState(null); // null=sin verificar; {puede,motivo}
  const esConsumible = (tipoActivo(tipos, form.productKind) || {}).arquetipo === 'consumible';
  useEffect(() => {
    if (!esConsumible || capComest) return undefined;
    let vivo = true;
    capacidadConsumible().then((c) => { if (vivo) setCapComest(c); }).catch(() => {});
    return () => { vivo = false; };
  }, [esConsumible, capComest]);
  const [draftId, setDraftId] = useState(initialDraft?._id || null);
  const [dirty, setDirty] = useState(false);
  const [saveState, setSaveState] = useState('idle'); // 'idle'|'saving'|'saved'|'error'|'dirty'
  const [savedAt, setSavedAt] = useState(null);
  const [pendingDraft, setPendingDraft] = useState(null); // borrador a retomar (edición)
  const [variations, setVariations] = useState([]);       // crudo del GET (imágenes por variación)
  // Tanda 4: filas EDITABLES de la tabla de variaciones + attributes del padre
  // (necesarios para renombrar un tono y quitar opciones huérfanas).
  const [vRows, setVRows] = useState([]);
  const [attrsWoo, setAttrsWoo] = useState([]);
  const vRowsRef = useRef(vRows); vRowsRef.current = vRows;
  const attrsWooRef = useRef(attrsWoo); attrsWooRef.current = attrsWoo;
  // SKU propio (no alertar contra sí mismo). Tanda 3e (defecto 1): un borrador
  // ENLAZADO también trae identidad — su SKU es el del producto de Woo, así que
  // el aviso de duplicado no puede disparar contra él.
  const [ownSku, setOwnSku] = useState(() => (
    initialDraft && initialDraft.wcProductId != null ? String(initialDraft.payload?.sku || '') : ''
  ));
  const [wooStatus, setWooStatus] = useState('');         // estado en WooCommerce (subtítulo de edición)
  // P3-10 punto 2: frescura de los datos — { fuente:'woo'|'espejo'|'espejo-stale', haceSeg }.
  const [frescura, setFrescura] = useState(null);
  const [actualizandoTienda, setActualizandoTienda] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [confirmDlg, setConfirmDlg] = useState(null);      // { title, message, confirmLabel, onYes }
  // Tanda 7 (PARTE B): diálogo PROPIO de "Publicar con N recomendaciones
  // pendientes" (lista vertical con detalle; no es el confirm genérico).
  const [publicarDlg, setPublicarDlg] = useState(null);    // { pendientes } | null
  // Tanda 3b (ajuste 9): estado elegido para el próximo guardado (selector).
  const [estado, setEstado] = useState('draft');
  // Tanda 3b (defecto 1a): resultado del último guardado en WooCommerce.
  const [wooSaved, setWooSaved] = useState(null);          // { at, status } | null
  const [saveError, setSaveError] = useState(null);        // Error real del POST/PUT (con .code de Woo)
  // Tanda 3d (defecto 1): el id de Woo viene TAMBIÉN del borrador retomado — un
  // borrador enlazado (wcProductId) se edita con PUT desde la primera apertura,
  // nunca con un segundo POST que choque por SKU duplicado.
  const [wooId, setWooId] = useState(initialDraft && initialDraft.wcProductId != null ? initialDraft.wcProductId : null);
  const [categoriesDirty, setCategoriesDirty] = useState(false); // item 1: solo envía categories si se tocó
  // Tanda 3g (decisión de Miguel): la regla de categorías generalizada — en el
  // PUT, los campos de texto viajan SOLO si el usuario los modificó (incluido
  // vaciarlos a propósito). Un borrador retomado/abierto cuenta como contenido
  // elegido: sus campos entran tocados (todos con initialDraft; en retomar,
  // solo los que difieren de la base de Woo).
  const [camposTocados, setCamposTocados] = useState(() => (
    initialDraft ? Object.fromEntries(CAMPOS_TEXTO_TOCABLES.map((k) => [k, true])) : {}
  ));
  const camposTocadosRef = useRef(camposTocados); camposTocadosRef.current = camposTocados;
  const marcarTocados = useCallback((patch) => {
    const claves = CAMPOS_TEXTO_TOCABLES.filter((k) => k in patch);
    if (!claves.length) return;
    setCamposTocados((prev) => {
      const next = { ...prev };
      claves.forEach((k) => { next[k] = true; });
      camposTocadosRef.current = next; // imperativo: doWrite puede correr antes del render
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // P3-1 (bug 1.1, medido en e2e A5): claves del form editadas y SIN guardar.
  // Más ancho que camposTocados (que rige el «solo tocados» del PUT): acá
  // entran también precio, stock, imágenes, categorías y el selector de estado.
  // Toda RELECTURA de la ficha fusiona conservando estas claves
  // (productMergeSucios) en vez de pisar el form.
  const suciosRef = useRef(new Set());
  const registrarSucios = useCallback((claves) => {
    claves.forEach((k) => suciosRef.current.add(k));
  }, []);
  // Última escritura propia de ESTA ficha (asociar foto, alta/borrado de
  // variación, Guardar): la actualización que llega enseguida por SSE es el
  // eco propio y no merece el aviso de «la tienda actualizó».
  const ultimaEscrituraPropiaRef = useRef(0);
  const marcarEscrituraPropia = useCallback(() => { ultimaEscrituraPropiaRef.current = Date.now(); }, []);
  const [avisoTienda, setAvisoTienda] = useState(false);
  // P3-9a: descarga del .zip de fotos del producto (sin recomprimir).
  const [descargandoFotos, setDescargandoFotos] = useState(false);
  const descargarFotos = useCallback(async () => {
    if (descargandoFotos) return;
    setDescargandoFotos(true);
    try { await descargarFotosProducto(productId, formRef.current.slug || formRef.current.name); }
    catch (e) { showToast({ message: e.message || 'No se pudieron descargar las fotos', variant: 'error' }); }
    finally { setDescargandoFotos(false); }
  }, [productId, descargandoFotos, showToast]);
  // P3-9b (preview, SIN escribir): reprocesa cada foto del producto en memoria,
  // mide el peso nuevo REAL y aplica el criterio (sobredimensionada o JPG ≥30% más
  // liviano). No sube ni reasigna nada — es la vista previa que decide con números
  // de verdad. El «optimizar» (escritura) es la tanda siguiente.
  const [prevEstado, setPrevEstado] = useState('idle'); // 'idle' | 'corriendo' | 'listo'
  const [prevResultados, setPrevResultados] = useState([]);
  const [prevProgreso, setPrevProgreso] = useState({ hecho: 0, total: 0 });
  const [prevResumen, setPrevResumen] = useState(null);
  const [prevUsos, setPrevUsos] = useState({});
  const correrPreviewFotos = useCallback(async () => {
    if (prevEstado === 'corriendo') return;
    const medias = reunirMediaProducto({ images: formRef.current.images || [], variations: variationsRef.current || [] });
    setPrevEstado('corriendo'); setPrevResultados([]); setPrevResumen(null); setPrevUsos({});
    setPrevProgreso({ hecho: 0, total: medias.length });
    // Reúso cross-product en paralelo (para el aviso «todas o ninguna»).
    fotosUsos(medias.map((m) => m.url)).then(setPrevUsos).catch(() => {});
    try {
      const res = await medirLista(medias, {
        prop: propFotoRef.current, // P3-12: techo según la proporción del tipo
        onProgreso: (hecho, total, r) => { setPrevProgreso({ hecho, total }); setPrevResultados((prev) => [...prev, r]); },
      });
      setPrevResumen(resumirPreview(res));
    } catch (e) {
      showToast({ message: e.message || 'No se pudo calcular el preview', variant: 'error' });
    } finally {
      setPrevEstado('listo');
    }
  }, [prevEstado, showToast]);
  // P3-9b (ESCRITURA): estado del optimizado (el handler vive más abajo, tras
  // `recargar`, para no caer en TDZ al referenciarlo en sus deps).
  const [optimizando, setOptimizando] = useState(false);
  const [optProgreso, setOptProgreso] = useState({ hecho: 0, total: 0, actual: '' });
  // Avance POR FOTO (clave = url): 'cola' | 'optimizando' | 'lista' | 'error'.
  const [optEstados, setOptEstados] = useState({});
  // Histórico de optimización de ESTE producto desde la base de datos: una línea
  // por corrida + total acumulado ({ corridas:[...], total }).
  const [optHistorial, setOptHistorial] = useState({ corridas: [], total: { fotos: 0, errores: 0, de: 0, a: 0, ahorro: 0 } });
  const [optHistorialAbierto, setOptHistorialAbierto] = useState(false);
  const candidatasOptimizar = prevResultados.filter((r) => r && r.ok && r.reprocesar);
  // P3-9b: filas del preview por URL, para el distintivo de la galería/variaciones.
  const prevPorUrl = {};
  prevResultados.forEach((r) => { if (r && r.url) prevPorUrl[r.url] = r; });
  // P3-9b: «optimizada» (del registro) por media id — galería + variaciones.
  const optimizadaPorId = {};
  (form.images || []).forEach((im) => { if (im && im.id != null) optimizadaPorId[im.id] = !!im.optimizada; });
  (variations || []).forEach((v) => { if (v.image && v.image.id != null) optimizadaPorId[v.image.id] = !!v.image.optimizada; });
  // P3-9b: fotos con texto alternativo VACÍO (galería + variaciones), distintas.
  const sinAltIds = new Set();
  (form.images || []).forEach((im) => { if (im && im.id != null && !(im.alt || '').trim()) sinAltIds.add(im.id); });
  (variations || []).forEach((v) => { if (v.image && v.image.id != null && !(v.image.alt || '').trim()) sinAltIds.add(v.image.id); });
  const sinAltCount = sinAltIds.size;
  const [completandoAlt, setCompletandoAlt] = useState(false);
  const [descMode, setDescMode] = useState('guiada');      // 'guiada' | 'plano' (item 3)
  const [descPlano, setDescPlano] = useState('');          // buffer de edición para descripción no mapeable
  const [imagesDirty, setImagesDirty] = useState(false);   // item 17: solo envía images si se tocó
  const [imgSel, setImgSel] = useState(null);              // { target:'main'|'gallery'|'variation', variationId? }
  const [moreOpen, setMoreOpen] = useState(false);         // ajuste 6: menú secundario (Eliminar)
  // Tanda 5: guía de tallas — resolución del árbitro del theme para ESTE
  // producto (null = consultando), la lista para "Usar otra guía" (se pide al
  // desplegar) y si el editor de tabla propia está abierto.
  const [guiaInfo, setGuiaInfo] = useState(null);
  const [guiasParaElegir, setGuiasParaElegir] = useState(null);
  const [medidasAbierta, setMedidasAbierta] = useState(false);
  // Tanda 7b: modal de alta de variación (el eje se pide solo la primera vez).
  const [altaVar, setAltaVar] = useState(false);
  // Tanda 7c (punto 2): lista CANÓNICA de ejes (Configuración → Catálogo);
  // si la config no responde rige la semilla espejo (EJES_INICIALES).
  const [ejesCanonicos, setEjesCanonicos] = useState(null);
  // P3-4: listas de marcas y colecciones para los selectores de la ficha.
  const [marcasLista, setMarcasLista] = useState([]);
  const [coleccionesLista, setColeccionesLista] = useState([]);
  // P3-7: ejes con sus términos EN ORDEN PÚBLICO (menu_order), para numerar las
  // filas de variación siguiendo el orden que ve el cliente. El orden se cambia
  // en Configuración → Catálogo (global), no acá.
  const [ejesLista, setEjesLista] = useState([]);
  useEffect(() => {
    let vivo = true;
    listarMarcas().then((m) => { if (vivo) setMarcasLista(Array.isArray(m) ? m : []); }).catch(() => {});
    listarColecciones().then((c) => { if (vivo) setColeccionesLista(Array.isArray(c) ? c : []); }).catch(() => {});
    listarEjes().then((e) => { if (vivo) setEjesLista(Array.isArray(e) ? e : []); }).catch(() => {});
    return () => { vivo = false; };
  }, []);
  useEffect(() => {
    // typeof: en jsdom sin stub de fetch el efecto no debe tumbar la suite —
    // sin config rige la semilla espejo, igual que si la red fallara.
    if (typeof fetch !== 'function') return undefined;
    let cancelado = false;
    fetch(`${API_URL}/api/config/ejes-variacion`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelado && d && Array.isArray(d.ejes) && d.ejes.length) setEjesCanonicos(d.ejes); })
      .catch(() => {});
    return () => { cancelado = true; };
  }, []);
  // Tanda 7: los relacionados viven en form.crossSellIds (editable, PARTE A);
  // el evaluador los cuenta desde ahí — el state crossSellCount de la tanda 6
  // se retiró.

  // Refs para el guardado al salir (onBack) sin depender del render.
  const formRef = useRef(form);   formRef.current = form;
  const variationsRef = useRef(variations); variationsRef.current = variations; // P3-9b preview: leer variaciones en el callback
  const optEstadosRef = useRef({}); optEstadosRef.current = optEstados; // P3-9b: estados por foto (reintento merge)
  const loteIdRef = useRef(null); // P3-9b: id de la tanda de optimización en curso (reintentos la reusan)
  const dirtyRef = useRef(dirty); dirtyRef.current = dirty;
  const draftIdRef = useRef(draftId); draftIdRef.current = draftId;
  const wooIdRef = useRef(wooId); wooIdRef.current = wooId;
  const wooStatusRef = useRef(wooStatus); wooStatusRef.current = wooStatus;
  // P3-10 punto 1 (optimistic lock): lo que la ficha tenía al cargar — fecha de
  // modificación en la tienda + snapshot de campos escalares. Viaja en el PUT para
  // detectar que alguien cambió el producto por fuera mientras se editaba.
  const baseLockRef = useRef({ dateModified: null, valores: {} });
  const [conflicto, setConflicto] = useState(null); // { cambios, dateModified } cuando el guardado choca
  // Tanda 7c (punto 2): {de, a} cuando el eje leído de Woo difiere del canónico
  // (doWrite lo consume para escribir el nombre corregido); null si no aplica.
  const ejeNormRef = useRef(null);
  const correccionesEjesRef = useRef([]); // tanda 8: [{de,a}] por eje corrigible (multi-eje)
  const autosaveTimer = useRef(null);
  const publishingRef = useRef(false); // guardado a Woo EN VUELO (el autosave no escribe mientras tanto)
  // Tanda 3e (defecto 2): el guardado exitoso elimina TODO borrador local del
  // producto. Estos dos refs son los cabos que doWrite necesita para eso:
  // el id del borrador PENDIENTE (aunque el banner se haya ignorado u ocultado)
  // y la promesa del saveDraft en vuelo (para no borrar antes de que exista).
  const pendingDraftIdRef = useRef(null);
  const draftSaveRef = useRef(null);

  // Aplica el GET /full a TODOS los estados de la ficha — lo usan la carga
  // inicial y la RECARGA post-guardado (tanda 4, PARTE B: releer por GET y
  // refrescar la tabla). Una sola definición para las dos.
  // P3-1: con { conservarSucios: true } la relectura FUSIONA — los campos
  // sucios (editados sin guardar) conservan lo del usuario, el resto toma lo
  // fresco; las filas de variaciones tocadas conservan sus valores y marcas.
  // Sin la opción (carga inicial, post-Guardar con sucios ya limpios) pisa
  // entero, como siempre.
  const aplicarProductoCompleto = useCallback((full, { conservarSucios = false } = {}) => {
    const fusionar = conservarSucios && suciosRef.current.size > 0;
    const f = productToForm(full, categories, tiposRef.current);
    // Item 3: parsear la descripción a partes limpias (o a texto plano si no mapea).
    const parsed = parseDescription(f.description);
    const descSucia = fusionar && hayDescripcionSucia(suciosRef.current);
    if (parsed.mappable) {
      f.descParrafo = parsed.parrafo;
      f.descDetalles = parsed.detalles.join('\n');
      f.descCierre = parsed.cierre;
      if (!descSucia) setDescMode('guiada');
    } else if (!descSucia) {
      setDescMode('plano');
      setDescPlano(parsed.plain);
    }
    // P3-12 «Más opciones»: leer featured/visibilidad/oferta(Bogotá)/umbral/slug/default/reservas
    // del producto. Se funde ANTES de fusionarFormConSucios para que un campo sucio del usuario gane.
    Object.assign(f, masOpcionesDesdeFull(full));
    const aplicado = fusionar ? fusionarFormConSucios(f, formRef.current, suciosRef.current) : f;
    setForm(aplicado);
    setVariations(full.variations || []);
    const frescas = (full.variations || []).map(filaAFormulario);
    setVRows(fusionar ? fusionarVRowsConSucios(frescas, vRowsRef.current) : frescas);
    setAttrsWoo(full.attributes || []);
    setOwnSku(full.sku || '');
    setWooStatus(full.status || '');
    // P3-10 punto 2: de dónde salió este GET (espejo fresco / Woo en vivo / espejo
    // viejo porque Woo no respondió) y hace cuánto se sincronizó. El backend lo
    // rotula en _fuente / _espejo_hace_seg.
    setFrescura({ fuente: full._fuente || 'woo', haceSeg: full._espejo_hace_seg ?? 0 });
    // P3-10 punto 1: base del optimistic lock SIEMPRE del valor fresco de la tienda
    // (no del merge con sucios): es contra lo que estaba en Woo que se compara luego.
    if (full.id != null) {
      baseLockRef.current = {
        dateModified: full.date_modified_gmt || null,
        valores: {
          name: full.name || '', status: full.status || '', sku: full.sku || '',
          regular_price: full.regular_price || '', sale_price: full.sale_price || '',
          stock_quantity: full.stock_quantity ?? null,
        },
      };
    }
    // ajuste 9: al editar muestra el estado actual — salvo que el usuario ya
    // haya elegido otro estado para el próximo guardado (sucio).
    if (!(fusionar && suciosRef.current.has('estado'))) setEstado(full.status || 'draft');
    return aplicado;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories]);

  // P3-10 punto 2: con { forzar: true } va a Woo en vivo (botón «Actualizar desde
  // la tienda»); sin él, el backend decide espejo-vs-Woo por frescura.
  const recargar = useCallback(async (pid, opts = {}) => {
    try {
      const url = `${API_URL}/api/products/${pid}/full${opts.forzar ? '?forzar=1' : ''}`;
      const res = await fetch(url);
      if (res.ok) aplicarProductoCompleto(await res.json(), opts);
    } catch { /* la vista conserva lo que tenía */ }
  }, [aplicarProductoCompleto]);

  // P3-5c: cargar la sección COMESTIBLE desde la tienda al abrir un consumible existente. El
  // backend (GET /api/consumibles/:id) arma el modelo con las 13 metas (del espejo) y el term meta
  // de sabor/pack EN VIVO (hex/descriptor/foto/sello); acá se vuelca al estado del formulario.
  const cargarComestible = useCallback(async (pid) => {
    try {
      const { modelo } = await leerConsumible(pid);
      setComestible(comestibleDesdeModelo(modelo || {}));
    } catch (e) {
      showToast({ message: `No se pudieron cargar los datos del comestible: ${e.message}`, variant: 'warning' });
    }
  }, [showToast]);

  // Botón «Actualizar desde la tienda»: trae de Woo en vivo, conservando lo que el
  // usuario esté editando sin guardar (campos sucios de P3-1).
  const actualizarDesdeTienda = useCallback(async () => {
    if (actualizandoTienda) return;
    setActualizandoTienda(true);
    try { await recargar(productId, { conservarSucios: true, forzar: true }); }
    finally { setActualizandoTienda(false); }
  }, [productId, recargar, actualizandoTienda]);

  // P3-9b (ESCRITURA): optimiza una lista de fotos candidatas — por cada una sube el
  // JPG nuevo (nombre SEO + alt), el backend reapunta TODAS sus apariciones (galería
  // + variaciones, en todos los productos que la usan o ninguno) y conserva la vieja
  // para revertir. EN SERIE (gentil con HostGator); cada foto reporta su estado
  // (cola → optimizando → lista/error) y al terminar deja un RESUMEN persistido.
  // Definido tras `recargar` para no caer en TDZ.
  const ejecutarOptimizacion = useCallback(async (candidatas) => {
    if (!candidatas.length) return;
    setOptimizando(true);
    marcarEscrituraPropia(); // los SSE del propio cambio son eco (P3-1)
    // Alt actual de cada foto de la galería (para preservarlo al subir el adjunto
    // nuevo; las que lo tienen VACÍO se rellenan con el nombre del producto — el
    // respaldo de P3-7 era solo visual y no llenaba el campo guardado).
    const altPorUrl = {};
    (formRef.current.images || []).forEach((im) => { if (im && im.src) altPorUrl[im.src] = im.alt || ''; });
    // Punto de partida: las candidatas de ESTA corrida a 'cola' (merge con lo previo).
    const estados = { ...optEstadosRef.current };
    for (const c of candidatas) estados[c.url] = { estado: 'cola', pesoOriginal: c.pesoOriginal, nombre: (c.usos || [])[0] || 'Foto' };
    setOptEstados({ ...estados });
    setOptProgreso({ hecho: 0, total: candidatas.length, actual: '' });
    let hechas = 0;
    for (let i = 0; i < candidatas.length; i++) {
      const c = candidatas[i];
      estados[c.url] = { ...estados[c.url], estado: 'optimizando' };
      setOptEstados({ ...estados }); setOptProgreso({ hecho: hechas, total: candidatas.length, actual: estados[c.url].nombre });
      try {
        // eslint-disable-next-line no-await-in-loop
        const rep = await reprocesarParaSubir(c, { prop: propFotoRef.current }); // P3-12: techo por proporción
        // eslint-disable-next-line no-await-in-loop
        await optimizarFoto({
          imageBase64: rep.base64, imageMime: rep.mime,
          filename: normalizeFilename(formRef.current.name || 'producto'),
          // Preserva el alt existente; si está vacío, usa el nombre del producto.
          altText: (altPorUrl[c.url] || '').trim() || generateAltImagen(formRef.current.name || ''),
          viejoId: c.id, viejoSrc: c.url, loteId: loteIdRef.current, etiqueta: (c.usos || []).join(', '),
          pesoViejo: c.pesoOriginal, pesoNuevo: rep.pesoNuevo, disparadoDesde: productId,
        });
        estados[c.url] = { ...estados[c.url], estado: 'lista', pesoNuevo: rep.pesoNuevo, error: null };
        hechas += 1;
      } catch (e) {
        estados[c.url] = { ...estados[c.url], estado: 'error', error: e.message };
        // Registra el fallo en la base de datos para que la corrida lo muestre en el histórico.
        // eslint-disable-next-line no-await-in-loop
        await registrarOptimizacionFallo({ loteId: loteIdRef.current, disparadoDesde: productId, viejoId: c.id, viejoSrc: c.url, error: e.message, nombre: (c.usos || [])[0] || '' });
      }
      setOptEstados({ ...estados }); setOptProgreso({ hecho: hechas, total: candidatas.length, actual: '' });
    }
    setOptimizando(false);
    // El contador in-session (con las fallidas) va al toast; el RESUMEN persistente
    // sale de la base de datos (FotoOptimizada), visible desde cualquier equipo.
    const res = resumenOptimizacion(estados);
    showToast({ message: `${res.fotos} foto(s) optimizada(s)${res.fallidas ? ` · ${res.fallidas} fallaron` : ''}. La original queda guardada por si hay que revertir.`, variant: res.fallidas ? 'warning' : 'success' });
    // Regla única (ninguna foto sin alt): completar el alt vacío de las fotos que se
    // dejaron igual («Cumple») — las optimizadas ya recibieron alt al subirse.
    try { await completarTextosAlternativos(productId); } catch { /* best-effort */ }
    // Relee el producto (galería/variaciones ya apuntan a las nuevas) y el histórico desde la DB.
    if (isEdit) await recargar(productId);
    historialOptimizacionProducto(productId).then(setOptHistorial).catch(() => {});
    setOptHistorialAbierto(true); // tras optimizar, mostrarlo
    onSaved && onSaved();
  }, [productId, isEdit, recargar, onSaved, showToast, marcarEscrituraPropia]);
  const optimizarFotos = useCallback(() => {
    if (optimizando) return;
    loteIdRef.current = `${productId}-${Date.now()}`; // nueva tanda (los reintentos la reusan)
    ejecutarOptimizacion(prevResultados.filter((r) => r && r.ok && r.reprocesar));
  }, [optimizando, prevResultados, productId, ejecutarOptimizacion]);
  // P3-9b: revertir una corrida (vuelve a las originales) y borrar las originales
  // (ya no se podrá revertir) — ambas con confirmación; al terminar releen la ficha
  // y el histórico.
  const refrescarHistorial = useCallback(() => { historialOptimizacionProducto(productId).then(setOptHistorial).catch(() => {}); }, [productId]);
  // id = loteId de la corrida, o `_<idRegistro>` para una sola foto. esFoto cambia el texto.
  const revertir = useCallback((id, esFoto = false) => {
    setConfirmDlg({
      title: esFoto ? 'Revertir esta foto' : 'Revertir toda la corrida', confirmLabel: 'Revertir',
      message: esFoto ? 'Vuelve a la foto original y borra la optimizada.' : 'Vuelve a las fotos originales y borra las optimizadas de esta corrida.',
      onYes: async () => {
        setConfirmDlg(null); marcarEscrituraPropia();
        try {
          const r = await revertirOptimizacion(id);
          showToast({ message: `Revertido (${r.revertidas} foto(s)).`, variant: 'success' });
          if (isEdit) await recargar(productId);
          refrescarHistorial(); onSaved && onSaved();
        } catch (e) { showToast({ message: e.message || 'No se pudo revertir', variant: 'error' }); }
      },
    });
  }, [productId, isEdit, recargar, refrescarHistorial, onSaved, showToast, marcarEscrituraPropia]);
  const borrarOriginales = useCallback((id, esFoto = false) => {
    setConfirmDlg({
      title: esFoto ? 'Borrar la foto original' : 'Borrar las fotos originales', confirmLabel: 'Borrar original',
      message: esFoto ? 'Elimina de la mediateca la foto original. Después NO se podrá revertir.' : 'Elimina de la mediateca las fotos originales de esta corrida. Después NO se podrá revertir.',
      onYes: async () => {
        setConfirmDlg(null);
        try {
          const r = await borrarOriginalesOptimizacion(id);
          showToast({ message: `${r.borradas} original(es) borrada(s).`, variant: 'success' });
          refrescarHistorial();
        } catch (e) { showToast({ message: e.message || 'No se pudieron borrar', variant: 'error' }); }
      },
    });
  }, [refrescarHistorial, showToast]);
  // P3-9b: completar los textos alternativos vacíos (nombre del producto, o nombre +
  // color de la variación), sin reemplazar los existentes. Con confirmación y conteo.
  const completarAlts = useCallback(() => {
    if (completandoAlt || sinAltCount === 0) return;
    setConfirmDlg({
      title: 'Completar textos alternativos', confirmLabel: `Completar ${sinAltCount}`,
      message: `${sinAltCount} foto${sinAltCount === 1 ? '' : 's'} sin texto alternativo. Se les pondrá el nombre del producto (o nombre + color en las variaciones), sin tocar las que ya tienen uno ni el archivo.`,
      onYes: async () => {
        setConfirmDlg(null); setCompletandoAlt(true); marcarEscrituraPropia();
        try {
          const r = await completarTextosAlternativos(productId);
          showToast({ message: `${r.completadas} texto(s) alternativo(s) completado(s).`, variant: 'success' });
          if (isEdit) await recargar(productId);
        } catch (e) { showToast({ message: e.message || 'No se pudieron completar', variant: 'error' }); }
        finally { setCompletandoAlt(false); }
      },
    });
  }, [completandoAlt, sinAltCount, productId, isEdit, recargar, showToast, marcarEscrituraPropia]);
  const reintentarOptimizar = useCallback((fila) => {
    if (optimizando || !fila) return;
    ejecutarOptimizacion([fila]);
  }, [optimizando, ejecutarOptimizacion]);
  // El histórico de optimización sale de la BASE DE DATOS (FotoOptimizada), así
  // cualquier usuario lo ve desde cualquier equipo al abrir la ficha.
  useEffect(() => {
    if (!isEdit || productId == null) return;
    historialOptimizacionProducto(productId).then(setOptHistorial).catch(() => {});
  }, [isEdit, productId]);

  // ── Carga en modo edición: producto + variaciones + borrador pendiente ───────
  useEffect(() => {
    if (!isEdit) return;
    let alive = true;
    (async () => {
      setLoading(true);
      let fullDoc = null;   // el producto de Woo — la BASE de la ficha (3e, defecto 1)
      let formWoo = null;
      try {
        const res = await fetch(`${API_URL}/api/products/${productId}/full`);
        if (res.ok) {
          const full = await res.json();
          if (alive) {
            fullDoc = full;
            formWoo = aplicarProductoCompleto(full);
            // P3-5c: si el producto es un consumible, llenar la sección Comestible desde la tienda.
            if ((full.meta_data || []).some((m) => m.key === '_vitrina_arquetipo' && m.value === 'consumible')) {
              await cargarComestible(productId);
            }
          }
        }
      } catch { /* la vista sigue utilizable con el form vacío */ }
      try {
        const drafts = await listDrafts({ wcProductId: productId });
        if (alive && Array.isArray(drafts) && drafts.length) {
          // El cabo queda SIEMPRE (el guardado exitoso lo elimina aunque el
          // banner no se ofrezca o se ignore)…
          pendingDraftIdRef.current = drafts[0]._id;
          // …pero el banner solo se OFRECE si el borrador es más reciente que
          // la última modificación en Woo Y difiere (3e, defecto 1: la base es
          // siempre el producto de Woo).
          if (draftEsOfrecible(drafts[0], fullDoc?.date_modified_gmt, formWoo)) setPendingDraft(drafts[0]);
        }
      } catch { /* sin borradores */ }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  // ── Tanda 5: guía de tallas — consultar el árbitro del theme ────────────────
  // Al cargar y tras cada guardado (wooSaved cambia). Solo en edición y solo si
  // el tipo de producto aplica guía de tallas.
  useEffect(() => {
    const aplica = !!(tipoActivo(tipos, form.productKind) || {}).aplicaTallas;
    // `loading`: sin el GET del producto todavía no se sabe el TIPO real — sin
    // esta espera, el default del form dispara una consulta con el tipo equivocado.
    if (!isEdit || loading || !aplica) { setGuiaInfo(null); return undefined; }
    let alive = true;
    guiaDeProducto(productId)
      .then((r) => { if (alive) setGuiaInfo(r); })
      .catch((e) => { if (alive) setGuiaInfo({ disponible: false, motivo: e.message || 'No se pudo consultar la guía de tallas' }); });
    return () => { alive = false; };
  }, [isEdit, loading, productId, form.productKind, tipos, wooSaved]);

  // P3-1: una actualización de productos que llega por SSE (webhook de la
  // tienda procesado por el backend, o una escritura desde otra superficie)
  // RE-LEE la ficha pero FUSIONA: los campos sucios se conservan. Si no es el
  // eco de una escritura propia reciente y hay cambios sin guardar, se avisa
  // discreto. Debounce: el backend emite varios data-changed por una sola
  // escritura (12 medidos en el e2e A3).
  const sseTimerRef = useRef(null);
  useEffect(() => {
    if (!isEdit) return undefined;
    const h = (e) => {
      const types = (e.detail && e.detail.types) || [];
      if (!types.includes('products')) return;
      if (publishingRef.current) return; // el guardado en vuelo ya relee al final
      if (sseTimerRef.current) clearTimeout(sseTimerRef.current);
      sseTimerRef.current = setTimeout(async () => {
        const esEco = Date.now() - ultimaEscrituraPropiaRef.current < 10_000;
        const habiaSucios = suciosRef.current.size > 0;
        await recargar(productId, { conservarSucios: true });
        if (!esEco && habiaSucios) setAvisoTienda(true);
      }, 800);
    };
    window.addEventListener('app:data-changed', h);
    return () => { window.removeEventListener('app:data-changed', h); if (sseTimerRef.current) clearTimeout(sseTimerRef.current); };
  }, [isEdit, productId, recargar]);

  // La lista para "Usar otra guía" se pide UNA vez, al desplegar.
  const cargarGuiasParaElegir = useCallback(() => {
    if (guiasParaElegir !== null) return;
    listarGuias()
      .then((r) => setGuiasParaElegir(r.disponible ? (r.guias || []) : []))
      .catch(() => setGuiasParaElegir([]));
  }, [guiasParaElegir]);

  // ── Autoguardado: SOLO si está sucio y el nombre no está vacío (item 10) ──────
  const doSave = useCallback(async () => {
    // Tanda 3d (defecto 1): con un guardado a Woo EN VUELO no se escribe el
    // borrador — wooId todavía no existe y el doc nacería huérfano (wcProductId
    // null). Es la carrera medida en T1 de productVinculoWoo.test.js.
    if (publishingRef.current) return;
    if (!dirtyRef.current || !String(formRef.current.name || '').trim()) return;
    setSaveState('saving');
    try {
      // La promesa y el id quedan en refs IMPERATIVOS (3e, defecto 2): doWrite
      // los necesita aunque el render con el estado nuevo todavía no corrió.
      // 3f (defecto 3b): si hay un borrador PENDIENTE sin retomar, se REUSA su
      // doc (PUT) en vez de crear otro — la revisión dejó DOS docs del mismo
      // producto acumulados; el contenido del pendiente se pisa a propósito
      // (editar sin retomarlo es elegir el contenido actual).
      draftSaveRef.current = saveDraft(formRef.current, { draftId: draftIdRef.current ?? pendingDraftIdRef.current, wcProductId: productId ?? wooIdRef.current });
      const saved = await draftSaveRef.current;
      if (saved && saved._id) { draftIdRef.current = saved._id; setDraftId(saved._id); }
      setDirty(false);
      setSaveState('saved');
      setSavedAt(new Date());
    } catch {
      setSaveState('error');
    } finally {
      draftSaveRef.current = null;
    }
  }, [productId]);

  const scheduleAutosave = useCallback(() => {
    // Un cambio nuevo deja atrás el último "Guardado en WooCommerce" (1a): el
    // banner solo afirma un estado que sigue siendo cierto.
    setWooSaved(null);
    // 3f: editar sin retomar el borrador pendiente es elegir el contenido
    // actual — la oferta se retira (su doc se va a REUSAR y quedaría mintiendo).
    setPendingDraft(null);
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => { doSave(); }, AUTOSAVE_MS);
  }, [doSave]);

  // Guardar al salir (desmontaje): solo si hay cambios y hay nombre. Tanda 3d
  // (defecto 1): con el producto ya creado en esta sesión (wooId), el borrador
  // sale ENLAZADO — antes iba con productId pelado (null en modo nuevo) y dejaba
  // un huérfano que el listado abría como "crear otro" → POST → SKU duplicado.
  useEffect(() => () => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    if (dirtyRef.current && String(formRef.current.name || '').trim()) {
      // 3f (defecto 3b): mismo reuso del doc pendiente que en doSave — el
      // guardado al salir no acumula un doc nuevo por visita.
      saveDraft(formRef.current, { draftId: draftIdRef.current ?? pendingDraftIdRef.current, wcProductId: productId ?? wooIdRef.current }).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = useCallback((patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    marcarTocados(patch); // 3g: lo que el usuario toca es lo que viaja en el PUT
    registrarSucios(Object.keys(patch)); // P3-1: y no se pisa en ninguna relectura
    setDirty(true);
    setSaveState('dirty');
    scheduleAutosave();
  }, [scheduleAutosave, marcarTocados, registrarSucios]);

  // Item 3: editar una parte de la plantilla guiada recompone la descripción HTML.
  const updateGuiada = useCallback((patch) => {
    setForm((prev) => {
      const next = { ...prev, ...patch };
      next.description = composeDescription(next);
      return next;
    });
    marcarTocados({ ...patch, description: true }); // 3g: la plantilla recompone description
    registrarSucios([...Object.keys(patch), 'description']); // P3-1
    setDirty(true); setSaveState('dirty'); scheduleAutosave();
  }, [scheduleAutosave, marcarTocados, registrarSucios]);

  // Item 3: editar la descripción en texto plano (formato no mapeable) → <p> al guardar.
  const updatePlano = useCallback((txt) => {
    setDescPlano(txt);
    update({ description: textToHtml(txt) });
  }, [update]);

  // Item 1: marcar/desmarcar una categoría del árbol (marca categoriesDirty).
  const toggleCategoria = useCallback((id) => {
    setCategoriesDirty(true);
    registrarSucios(['categoryIds']); // P3-1
    setForm((prev) => {
      const set = new Set(prev.categoryIds || []);
      if (set.has(id)) set.delete(id); else set.add(id);
      return { ...prev, categoryIds: [...set] };
    });
    setDirty(true); setSaveState('dirty'); scheduleAutosave();
  }, [scheduleAutosave, registrarSucios]);

  // ── Imágenes (item 17) — todo por id; quitar del producto NO borra de la mediateca ──
  const marcarImagenesSucias = useCallback(() => {
    setImagesDirty(true); registrarSucios(['images']); // P3-1
    setDirty(true); setSaveState('dirty'); scheduleAutosave();
  }, [scheduleAutosave, registrarSucios]);

  // P3-9a (remate): estado visual de la foto POR variación durante la ASOCIACIÓN
  // (saveVariationImage, un write a Woo). La celda muestra «Cargando…» (gris +
  // spinner), la miniatura al terminar, o el motivo + «Reintentar» si falla. No
  // bloquea el resto del formulario — es por variación.
  const [fotoVarEstado, setFotoVarEstado] = useState({}); // { [vid]: { estado, motivo, imageId, src } }
  const asociarFotoVariacion = useCallback((vid, imageId, src, optimizada = false) => {
    setFotoVarEstado((p) => ({ ...p, [vid]: { estado: 'cargando', imageId, src } }));
    marcarEscrituraPropia(); // el SSE que esto dispara es eco propio
    saveVariationImage(productId, vid, imageId)
      .then(() => {
        setVariations((prev) => prev.map((v) => (v.id === vid ? { ...v, image: { id: imageId, src, optimizada } } : v)));
        setFotoVarEstado((p) => { const n = { ...p }; delete n[vid]; return n; });
      })
      .catch((e) => {
        setFotoVarEstado((p) => ({ ...p, [vid]: { estado: 'error', motivo: e.message, imageId, src } }));
        reportarFalloSubida({ paso: 'asociacion', motivo: e.message, productoId: productId, variacionId: vid, archivo: src || '' });
      });
  }, [productId, marcarEscrituraPropia]);

  // Ajuste 7 (3c): onSelect entrega SIEMPRE un array (una o varias imágenes). No
  // cierra el diálogo — el selector lo cierra al terminar (y se queda abierto si
  // alguna subida falló). Acepta también un objeto suelto por compatibilidad.
  const onSeleccionImagen = useCallback((imgs) => {
    const list = Array.isArray(imgs) ? imgs.filter(Boolean) : (imgs ? [imgs] : []);
    if (!list.length) return;
    const target = imgSel?.target;
    const vid = imgSel?.variationId;
    if (target === 'variation' && vid != null) {
      // Imagen por variación: guarda SOLO image, de inmediato. P3-1 (punto 5):
      // si llegaron VARIAS, la primera queda en la variación y el resto va a la
      // GALERÍA con aviso — antes se descartaban en silencio (list[0] pelado).
      const { paraVariacion, paraGaleria } = repartirFotosParaVariacion(list);
      asociarFotoVariacion(vid, paraVariacion.id, paraVariacion.src, !!paraVariacion.optimizada); // estado en la celda
      if (paraGaleria.length) {
        setForm((prev) => {
          let next = [...(prev.images || [])];
          paraGaleria.forEach((img) => {
            next = next.filter((x) => x.id !== img.id);
            next = [...next, img];
          });
          return { ...prev, images: next };
        });
        marcarImagenesSucias();
        showToast({ message: `1 foto quedó en la variación; ${paraGaleria.length === 1 ? 'la otra se agregó' : `las otras ${paraGaleria.length} se agregaron`} a la galería.`, variant: 'info', duration: 6000 });
      }
      return;
    }
    setForm((prev) => {
      let next = [...(prev.images || [])];
      list.forEach((img, i) => {
        next = next.filter((x) => x.id !== img.id);      // sin duplicados
        if (target === 'main' && i === 0) next = [img, ...next]; // la primera queda principal
        else next = [...next, img];                      // el resto, a la galería
      });
      return { ...prev, images: next };
    });
    marcarImagenesSucias();
  }, [imgSel, marcarImagenesSucias, showToast, asociarFotoVariacion]);

  const quitarImagen = useCallback((id) => {
    setForm((prev) => ({ ...prev, images: (prev.images || []).filter((i) => i.id !== id) }));
    marcarImagenesSucias();
  }, [marcarImagenesSucias]);

  const hacerPrincipal = useCallback((id) => {
    setForm((prev) => {
      const list = prev.images || [];
      const img = list.find((i) => i.id === id);
      if (!img) return prev;
      return { ...prev, images: [img, ...list.filter((i) => i.id !== id)] };
    });
    marcarImagenesSucias();
  }, [marcarImagenesSucias]);

  const moverImagen = useCallback((from, to) => {
    // P3-7: el reorden vive en utils/reordenarLista (probado por mutación); acá
    // solo se aplica al form. El arrastre lo invoca onGalleryDragEnd.
    setForm((prev) => ({ ...prev, images: reordenarLista(prev.images || [], from, to) }));
    marcarImagenesSucias();
  }, [marcarImagenesSucias]);

  const editarAltImagen = useCallback((id, alt) => {
    setForm((prev) => ({ ...prev, images: (prev.images || []).map((i) => i.id === id ? { ...i, alt } : i) }));
    updateMediaAlt(id, alt).catch(() => {}); // persiste en la mediateca (best-effort)
  }, []);

  const usarPrincipalEnVariacion = useCallback((vid) => {
    const main = (formRef.current.images || [])[0];
    if (!main) { showToast({ message: 'Primero agrega una imagen principal.', variant: 'warning' }); return; }
    asociarFotoVariacion(vid, main.id, main.src); // mismo estado en la celda
  }, [showToast, asociarFotoVariacion]);

  // ── Usar / ignorar el borrador pendiente (edición) ──────────────────────────
  const retomar = () => {
    if (!pendingDraft) return;
    const df = draftToForm(pendingDraft);
    // P3-1: usar el borrador ES elegir su contenido como cambios sin guardar —
    // ninguna relectura puede pisarlo hasta el próximo Guardar.
    registrarSucios(Object.keys(df));
    // 3g: usar el borrador es ELEGIR su contenido — los campos de texto donde
    // difiere de la base de Woo quedan tocados y viajarán en el PUT.
    setCamposTocados((prev) => {
      const next = { ...prev };
      CAMPOS_TEXTO_TOCABLES.forEach((k) => {
        if (JSON.stringify(df[k]) !== JSON.stringify(formRef.current[k])) next[k] = true;
      });
      camposTocadosRef.current = next;
      return next;
    });
    setForm(df);
    draftIdRef.current = pendingDraft._id;
    setDraftId(pendingDraft._id);
    setPendingDraft(null);
    setSaveState('saved');
    setSavedAt(pendingDraft.updatedAt || new Date());
  };
  // "Ignorar" (3e, defecto 2) OCULTA el banner sin destruir nada: el borrador
  // muere recién con el próximo guardado exitoso (pendingDraftIdRef lo recuerda).
  // Destruir vive en el "Descartar" del encabezado, con confirmación.
  const ignorarPendiente = () => setPendingDraft(null);

  const irAlListado = () => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    onBack && onBack();
  };

  // ── Tres acciones (tanda 3b, ajuste 9): atrás / Descartar / Guardar ──────────

  // Atrás: sale y CONSERVA el borrador local (el efecto de desmontaje lo
  // guarda); si había cambios, avisa que quedó guardado.
  const handleBack = () => {
    // Defecto 3 (3c): el aviso sale si hay un borrador local que se conserva —
    // sea por cambios sin volcar (el efecto de desmontaje los guarda) o porque el
    // autoguardado (12s) ya creó el borrador. Antes solo miraba `dirty`, así que
    // editar más de 12s volvía SIN confirmación y parecía que nada se guardó.
    if (String(formRef.current.name || '').trim() && (dirtyRef.current || draftIdRef.current)) {
      showToast({ message: 'Borrador local guardado', variant: 'success', duration: 2500 });
    }
    irAlListado();
  };

  // Descartar: elimina el borrador local y sale, con confirmación (solo si hay
  // algo que perder: cambios o un borrador guardado).
  const handleDescartar = () => {
    const go = async () => {
      setDirty(false); dirtyRef.current = false;
      const id = draftIdRef.current || (pendingDraft && pendingDraft._id);
      if (id) { try { await deleteDraft(id); } catch { /* ignore */ } }
      irAlListado();
    };
    if (dirty || draftIdRef.current || pendingDraft) {
      setConfirmDlg({
        title: 'Descartar borrador',
        message: 'Vas a eliminar el borrador local y volver al listado. Lo que ya esté guardado en WooCommerce no se toca.',
        confirmLabel: 'Eliminar y salir',
        onYes: () => { setConfirmDlg(null); go(); },
      });
    } else { go(); }
  };

  // ── Eliminar producto (ajuste 6 de 3c + ajuste 4 de 3e). La confirmación
  // compartida (confirmacionDeEliminar) NOMBRA el producto y DICE cuál de los
  // dos caminos va a pasar: nunca publicado → definitivo (libera el SKU);
  // publicado → papelera (SKU ocupado). Visible junto a Descartar y en el menú.
  const handleEliminar = () => {
    setMoreOpen(false);
    const targetId = productId ?? wooIdRef.current;
    if (targetId == null) return;
    const nombre = String(formRef.current.name || '').trim() || 'este producto';
    const conf = confirmacionDeEliminar(nombre, wooStatusRef.current);
    setConfirmDlg({
      title: conf.title,
      message: conf.message,
      confirmLabel: conf.confirmLabel,
      onYes: async () => {
        setConfirmDlg(null);
        try {
          await deleteProductToWoo(targetId, { definitivo: conf.definitivo });
          const idsB = new Set([draftIdRef.current, pendingDraftIdRef.current].filter(Boolean));
          for (const idB of idsB) { try { await deleteDraft(idB); } catch { /* ignore */ } }
          showToast({
            message: conf.definitivo ? `"${nombre}" se eliminó definitivamente.` : `"${nombre}" se envió a la papelera.`,
            variant: 'success',
          });
          onSaved && onSaved();       // refresca el listado
          irAlListado();
        } catch (e) {
          showToast({ message: e.message, variant: 'error' });
        }
      },
    });
  };

  // ── Guardar en WooCommerce (defecto 1a: sin navegar; ajuste 9: con estado) ───
  const doWrite = useCallback(async (status, { forzarSobrescribir = false } = {}) => {
    // Tanda 3d (defecto 1): el timer de autosave se corta ANTES de escribir en
    // Woo, no después del await — si disparaba en pleno vuelo escribía un
    // borrador sin wcProductId (el huérfano de la revisión de 3c).
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    publishingRef.current = true;
    marcarEscrituraPropia(); // P3-1: el SSE del propio guardado es eco
    setPublishing(true);
    setConfirmDlg(null);
    setSaveError(null);
    setConflicto(null); // P3-10 punto 1: un nuevo intento limpia el conflicto anterior
    try {
      const targetId = productId ?? wooIdRef.current;
      // Tanda 4 (PARTE B): renombrar un tono escribe PRIMERO el padre
      // (attributes con el valor nuevo) y después la variación; si la segunda
      // falla, se REVIERTE el padre y se muestra el error real.
      const filas = vRowsRef.current;
      const multiEjeWrite = esMultiEje(attrsWooRef.current);
      // Tanda 8: en multi-eje las options del padre se recalculan como la UNIÓN
      // de los valores de las variaciones (los renombres de VALOR ya viven en
      // filas.opciones). La normalización del NOMBRE de eje corre por eje SOLO si
      // el flag está activo (tanda 8b: desactivada hasta J8 — renombrar el nombre
      // de un eje cuelga el read de Woo sin timeout). El camino mono-eje queda
      // EXACTAMENTE como la 7d (paridad garantizada por ramas separadas).
      const fixesEjes = (multiEjeWrite && NORMALIZACION_NOMBRE_EJE_MULTIEJE) ? (correccionesEjesRef.current || []) : [];
      const nombreFixEje = (nombre) => (fixesEjes.find((x) => x.de === nombre) || {}).a || nombre;
      let attrsNuevos = null;
      let renombres = [];
      let ejeFix = null;
      if (multiEjeWrite) {
        const algunEjeTocado = filas.some((f) => f.tocados.option);
        if (algunEjeTocado || fixesEjes.length) {
          const base = recalcularOpcionesPadre(attrsWooRef.current, filas);
          attrsNuevos = fixesEjes.length ? base.map((a) => ({ ...a, name: nombreFixEje(a.name) })) : base;
        }
      } else {
        // Tanda 4/7c mono-eje, intacto: renombre por fila + nombre canónico del eje.
        renombres = filas.filter((f) => f.tocados.option && f.option !== f.optionOriginal);
        attrsNuevos = renombres.length
          ? renombres.reduce((acc, f) => renombrarOpcion(acc, f.attributeName, f.optionOriginal, f.option), attrsWooRef.current)
          : null;
        ejeFix = ejeNormRef.current;
        if (ejeFix) {
          const base = attrsNuevos || attrsWooRef.current;
          attrsNuevos = base.map((a) => (a.name === ejeFix.de ? { ...a, name: ejeFix.a } : a));
        }
      }
      // P3-10 punto 1: en EDICIÓN mandamos la base del optimistic lock (fecha + snapshot
      // que había al abrir) y, si el usuario eligió «Sobrescribir», el flag para saltarlo.
      const baseLock = targetId != null ? baseLockRef.current : null;
      // P3-12: si el TIPO tiene orden de ejes activado, se aplica a attributes[].position al GUARDAR
      // (no es una reescritura masiva: viaja solo en el guardado de ESTE producto). Inerte si el tipo
      // no ordena (moTipoRef vacío → SH no cambia).
      if ((moTipoRef.current.ordenEjes || []).length >= 2) {
        const slugDe = (attr) => (ejesListaRef.current.find((e) => e.id === attr.id) || {}).slug || '';
        attrsNuevos = aplicarOrdenEjes(attrsNuevos || attrsWooRef.current || [], moTipoRef.current.ordenEjes, slugDe);
      }
      const saved = await saveProductToWoo(formRef.current, { productId: targetId, status, includeCategories: categoriesDirty, includeImages: imagesDirty, camposTocados: camposTocadosRef.current, attributes: attrsNuevos, tipos: tiposRef.current, base: baseLock, forzarSobrescribir });
      // Variaciones tocadas (regla 3g por fila): una → PUT; varias → UNA
      // llamada batch. Si falla, la reversión del padre va antes del error.
      const pidVar = targetId ?? saved.id;
      let plan = planDeEscrituras(filas);
      // Con un eje corregido, TODAS las variaciones deben quedar coherentes con
      // el nombre canónico, no solo el padre: se integra al plan (una pasada).
      if (multiEjeWrite && fixesEjes.length && pidVar != null && filas.length) {
        // Tanda 8: cada variación se reescribe con su SET COMPLETO y los nombres
        // de eje canónicos (conserva lo demás tocado de cada fila).
        const porId = new Map(plan.items.map((i) => [i.id, i]));
        for (const f of filas) {
          const item = { ...(porId.get(f.id) || { id: f.id }) };
          item.ejes = (f.opciones || []).map((o) => {
            const nom = nombreFixEje(o.name);
            // P2b tonos: id del eje GLOBAL (id>0) → Woo liga a la taxonomía; local NO lleva id.
            const a = attrsWooRef.current.find((x) => x.variation && (x.name === nom || x.name === o.name));
            const par = { name: nom, option: o.option };
            if (Number(a?.id) > 0) par.id = Number(a.id);
            return par;
          });
          delete item.option; delete item.attributeName;
          porId.set(f.id, item);
        }
        const items = [...porId.values()];
        plan = { modo: items.length === 1 ? 'put' : 'batch', items };
      } else if (!multiEjeWrite && ejeFix && pidVar != null && filas.length) {
        // Tanda 7c mono-eje, intacto.
        const porId = new Map(plan.items.map((i) => [i.id, i]));
        for (const f of filas) {
          const item = { ...(porId.get(f.id) || { id: f.id }) };
          item.attributeName = ejeFix.a;
          // P2b tonos: id del eje GLOBAL para que el renombre mono-eje no desligue la variación (local NO lleva id).
          if (item.attributeId == null) {
            const idEje = (attrsWooRef.current.find((a) => a.variation) || {}).id;
            if (Number(idEje) > 0) item.attributeId = Number(idEje);
          }
          if (item.option == null) item.option = f.option;
          porId.set(f.id, item);
        }
        const items = [...porId.values()];
        plan = { modo: items.length === 1 ? 'put' : 'batch', items };
      }
      // P3-12: en VARIABLE, la oferta/reservas/umbral de «Más opciones» se aplican a TODAS las
      // variaciones al guardar (solo si el tipo las activó Y el usuario las tocó). La oferta va solo
      // a las REBAJADAS (payloadOfertaVariacion). Se funde sobre el plan (creando items si faltan),
      // sin tocar el camino de renombres/ejes de arriba.
      if (formRef.current.type === 'variable' && pidVar != null && filas.length) {
        const tocd = camposTocadosRef.current || {};
        const mo = moTipoRef.current;
        const ponerOferta = mo.oferta && tocd.oferta;
        const ponerReservas = mo.reservas && tocd.backorders;
        const ponerUmbral = mo.umbral && tocd.lowStockAmount;
        if (ponerOferta || ponerReservas || ponerUmbral) {
          const porId = new Map((plan.items || []).map((i) => [i.id, i]));
          for (const f of filas) {
            const item = { ...(porId.get(f.id) || { id: f.id }) };
            if (ponerOferta) {
              const of = payloadOfertaVariacion({ sale_price: f.salePrice }, formRef.current, { tipo: tipoActivo(tiposRef.current, formRef.current.productKind) });
              if (of) Object.assign(item, of);
            }
            if (ponerReservas) item.backorders = formRef.current.backorders || 'no';
            if (ponerUmbral) item.low_stock_amount = String(formRef.current.lowStockAmount || '').trim() === '' ? null : parseInt(formRef.current.lowStockAmount, 10) || 0;
            porId.set(f.id, item);
          }
          const items = [...porId.values()];
          plan = { modo: items.length === 1 ? 'put' : 'batch', items };
        }
      }
      if (plan.modo !== 'nada' && pidVar != null) {
        try {
          if (plan.modo === 'put') await saveVariation(pidVar, plan.items[0]);
          else await saveVariationsBatch(pidVar, plan.items);
        } catch (eVar) {
          // Tanda 7c/8: si se tocó el padre (renombre, corrección de eje o unión
          // recalculada), se revierte — no puede quedar adelantado si las
          // variaciones no siguieron.
          if (renombres.length || ejeFix || (multiEjeWrite && attrsNuevos)) {
            try {
              await saveProductToWoo(formRef.current, { productId: pidVar, status, includeCategories: false, includeImages: false, camposTocados: {}, attributes: attrsWooRef.current, tipos: tiposRef.current });
            } catch { /* la reversión también falló; el error visible es el de la variación */ }
          }
          throw eVar;
        }
      }
      // Al guardar bien, se elimina TODO borrador local del producto (item 10 +
      // 3e defecto 2): el de esta sesión — esperando primero el saveDraft que
      // pudiera seguir en vuelo — Y el pendiente del banner, que sobrevivía si
      // no se retomaba (el "Guardado a las 09:41" con banner "del 09:34").
      if (draftSaveRef.current) { try { await draftSaveRef.current; } catch { /* ignore */ } }
      const idsBorrador = new Set([draftIdRef.current, pendingDraftIdRef.current].filter(Boolean));
      for (const idB of idsBorrador) { try { await deleteDraft(idB); } catch { /* ignore */ } }
      draftIdRef.current = null; pendingDraftIdRef.current = null;
      setDraftId(null); setPendingDraft(null);
      setDirty(false); dirtyRef.current = false;
      if (targetId == null && saved.id != null) {
        setWooId(saved.id); // el próximo guardado es PUT
        // …y el SKU recién creado pasa a ser PROPIO: el aviso de duplicado no
        // puede disparar contra el producto que esta sesión acaba de crear (3e).
        setOwnSku(String(formRef.current.sku || '').trim());
      }
      setWooStatus(saved.status || status);
      // La página NO navega sola: muestra el resultado y deja elegir (1a).
      setWooSaved({ at: new Date(), status: saved.status || status });
      // Tanda 4 (PARTE B): tras guardar en edición se RELEE por GET y se
      // refresca la tabla (los tocados quedan limpios con las filas frescas).
      setCamposTocados({}); camposTocadosRef.current = {};
      suciosRef.current = new Set(); // P3-1: lo guardado ya no es sucio — la relectura pisa entero
      setAvisoTienda(false);
      if (isEdit) await recargar(productId);
      onSaved && onSaved(); // refresca la copia local del listado en segundo plano
    } catch (err) {
      // P3-10 punto 1: el conflicto de edición NO es un error de guardado — abre el
      // panel de resolución (recargar / sobrescribir), sin pisar nada en la tienda.
      if (err && err.code === 'conflicto_edicion') {
        setConflicto(err.conflicto || { cambios: [], dateModified: null });
      } else {
        // Error real a la vista; el borrador local se conserva (1a).
        setSaveError(err);
      }
    } finally {
      publishingRef.current = false;
      setPublishing(false);
    }
  }, [productId, isEdit, recargar, onSaved, categoriesDirty, imagesDirty, marcarEscrituraPropia]);

  // P3-10 punto 1: resolución del conflicto.
  //  · Recargar: trae los valores frescos de la tienda conservando lo que el usuario
  //    estaba editando (campos sucios) y REFRESCA la base → el próximo guardado pasa.
  //  · Sobrescribir: reintenta el guardado con el flag que salta el chequeo (pisa el
  //    cambio externo a sabiendas).
  const resolverConflictoRecargar = useCallback(async () => {
    setConflicto(null);
    await recargar(productId, { conservarSucios: true, forzar: true });
  }, [productId, recargar]);
  const guardarComestibleRef = useRef(null); // P3-5c: para que «Sobrescribir» reintente el guardado del comestible
  const resolverConflictoSobrescribir = useCallback(async () => {
    if (esConsumible) { await guardarComestibleRef.current?.({ forzarSobrescribir: true }); return; }
    await doWrite(wooStatusRef.current || 'draft', { forzarSobrescribir: true });
  }, [doWrite, esConsumible]);

  // ── Tanda 4: edición de las filas de la tabla de variaciones ────────────────
  const setVRowCampo = useCallback((id, patch) => {
    setWooSaved(null);
    setVRows((prev) => prev.map((r) => {
      if (r.id !== id) return r;
      const marcas = {};
      CAMPOS_FILA_VARIACION.forEach((k) => { if (k in patch) marcas[k] = true; });
      return { ...r, ...patch, tocados: { ...r.tocados, ...marcas } };
    }));
  }, []);

  // Tanda 8: edición de un eje en un producto multi-eje. El texto en curso vive
  // en `draftEje` (clave filaId|eje) — las filas NO se tocan hasta el commit,
  // así el «valor viejo» y el conteo de afectadas se miden sobre el estado real.
  const [draftEje, setDraftEje] = useState({});
  const claveEje = (id, eje) => `${id}|${eje}`;
  // `valorNuevo` llega del evento (blur/Enter), NO del estado draftEje: en React
  // real el setState del onChange puede no haber re-render antes del blur, y una
  // closure sobre draftEje leería el valor viejo (verde en jsdom, roto en vivo).
  const commitRenombreEje = useCallback((fila, ejeName, valorNuevo) => {
    const clave = claveEje(fila.id, ejeName);
    setDraftEje((prev) => {
      const rest = { ...prev }; delete rest[clave]; return rest; // el draft se consume
    });
    const de = ((fila.opciones || []).find((o) => o.name === ejeName) || {}).option || '';
    const a = String(valorNuevo != null ? valorNuevo : de).trim();
    if (!a || a === de) return; // sin cambio
    const filas = vRowsRef.current;
    if (renombreHaciaExistente(filas, ejeName, de, a)) {
      showToast({ message: `"${a}" ya existe en ${ejeName}. Para crear otra combinación usa "Agregar variación".`, variant: 'warning' });
      return;
    }
    const aplicar = () => {
      setWooSaved(null);
      setVRows((prev) => aplicarRenombreEje(prev, ejeName, de, a));
    };
    const afectadas = afectadasPorRenombre(filas, ejeName, de);
    if (afectadas >= 2) {
      setConfirmDlg({
        title: 'Renombrar un valor compartido',
        message: `"${de}" está en ${afectadas} variaciones. Renombrarlo a "${a}" las cambia todas.`,
        confirmLabel: `Renombrar en ${afectadas}`,
        onYes: () => { setConfirmDlg(null); aplicar(); },
      });
    } else {
      aplicar();
    }
  }, [showToast]);

  // Tanda 8: el SKU combina los ejes cuando hay más de uno (PAD-NEGRO-S); con
  // uno solo pasa el string y es byte-idéntico a la 7d.
  const valorParaSku = (r) => ((r.opciones || []).length > 1 ? r.opciones : r.option);
  const generarSkuFila = useCallback((id) => {
    setVRows((prev) => prev.map((r) => (
      r.id === id
        ? { ...r, sku: generateVariationSku(formRef.current.sku, valorParaSku(r)), tocados: { ...r.tocados, sku: true } }
        : r
    )));
  }, []);
  const generarSkuTodas = useCallback(() => {
    setVRows((prev) => prev.map((r) => ({
      ...r, sku: generateVariationSku(formRef.current.sku, valorParaSku(r)), tocados: { ...r.tocados, sku: true },
    })));
  }, []);

  // Eliminar variación: confirmación con nombre, SKU y stock (advierte si hay
  // stock); DELETE force y, si la opción queda huérfana, se quita del padre.
  const eliminarVariacion = useCallback((fila) => {
    const pid = productId ?? wooIdRef.current;
    if (pid == null) return;
    const stock = parseInt(fila.stockQty, 10) || 0;
    setConfirmDlg({
      title: 'Eliminar variación',
      message: `Vas a eliminar la variación "${fila.optionOriginal || fila.option}" (SKU ${fila.sku || 'sin SKU'}).` +
        (stock > 0 ? ` Ojo: todavía tiene ${stock} ${stock === 1 ? 'unidad' : 'unidades'} de stock.` : '') +
        ' Esta acción no se puede deshacer.',
      confirmLabel: 'Eliminar variación',
      onYes: async () => {
        setConfirmDlg(null);
        try {
          marcarEscrituraPropia(); // antes de la primera escritura (ver altaVariacion)
          await deleteVariation(pid, fila.id);
          if (opcionQuedaHuerfana(vRowsRef.current, fila.id, fila.optionOriginal)) {
            const attrsSin = attrsWooRef.current.map((a) => (
              a.name === fila.attributeName
                ? { ...a, options: (a.options || []).filter((o) => o !== fila.optionOriginal) }
                : a
            ));
            await saveProductToWoo(formRef.current, { productId: pid, status: wooStatusRef.current || 'draft', includeCategories: false, includeImages: false, camposTocados: {}, attributes: attrsSin, tipos: tiposRef.current });
          }
          showToast({ message: `Variación "${fila.optionOriginal || fila.option}" eliminada.`, variant: 'success' });
          await recargar(pid, { conservarSucios: true }); // P3-1: no pisa lo editado sin guardar
          onSaved && onSaved();
        } catch (e) {
          showToast({ message: e.message, variant: 'error' });
        }
      },
    });
  }, [productId, recargar, onSaved, showToast, marcarEscrituraPropia]);

  // P3-5b: Guardar de un COMESTIBLE — traduce al modelo de Neto y lo manda al motor del
  // backend (valida primero; los errores por campo se muestran en la sección). No pasa
  // por el camino genérico de Woo.
  const guardarComestible = async ({ forzarSobrescribir = false } = {}) => {
    if (capComest && !capComest.puede) { showToast({ message: capComest.motivo || 'Esta tienda no puede guardar comestibles.', variant: 'warning' }); return; }
    // Validación BÁSICA en el formulario, antes de molestar al motor.
    const basicos = validarComestibleBasico(formRef.current, comestibleRef.current);
    if (basicos.length) { setErroresComest(basicos); setTab('comestible'); showToast({ message: 'Faltan datos del comestible — revisa lo marcado.', variant: 'warning' }); return; }
    const modelo = comestibleAModelo(formRef.current, comestibleRef.current);
    // P3-5c (409 de P3-10): en edición viaja la base (date_modified_gmt + valores) que la ficha
    // tenía al abrir; si el producto cambió en la tienda, el motor responde 409 y abre el diálogo
    // Recargar/Sobrescribir — el MISMO de los productos. «Sobrescribir» reintenta con el flag.
    if (isEdit) {
      modelo.base_date_modified_gmt = baseLockRef.current.dateModified || null;
      modelo.base_valores = baseLockRef.current.valores || {};
      if (forzarSobrescribir) modelo.forzar_sobrescribir = true;
    }
    setPublishing(true);
    setConflicto(null);
    try {
      const val = await validarConsumible(modelo);
      if (!val.ok) { setErroresComest(traducirErroresMotor(val.errores)); setTab('comestible'); showToast({ message: 'Revisa los campos marcados del comestible.', variant: 'warning' }); return; }
      setErroresComest([]);
      marcarEscrituraPropia();
      const r = isEdit ? await actualizarConsumible(productId, modelo) : await crearConsumible(modelo);
      showToast({ message: isEdit ? 'Comestible actualizado.' : `Comestible creado (#${r.productoId}).`, variant: 'success' });
      if (onSaved) onSaved();
      if (isEdit) { await recargar(productId); await cargarComestible(productId); }
    } catch (e) {
      if (e.conflicto) { setConflicto(e.conflicto); } // P3-5c: no es error — abre el diálogo de conflicto
      else if (e.tipo === 'validacion' && e.errores) { setErroresComest(traducirErroresMotor(e.errores)); setTab('comestible'); showToast({ message: 'Revisa los campos marcados.', variant: 'warning' }); }
      else { showToast({ message: e.message || 'No se pudo guardar el comestible.', variant: 'error' }); }
    } finally { setPublishing(false); }
  };
  guardarComestibleRef.current = guardarComestible; // P3-5c: «Sobrescribir» llama al guardado fresco

  const guardar = () => {
    if (esConsumible) { guardarComestible(); return; }
    const missing = requiredToSaveWoo(formRef.current);
    if (missing.length) {
      showToast({ message: `Para guardar falta: ${missing.join(', ')}.`, variant: 'warning' });
      return;
    }
    const pasaAPublicado = estado === 'publish' && wooStatusRef.current !== 'publish';
    if (pasaAPublicado) {
      // Tanda 7 (PARTE B): con OBLIGATORIAS pendientes según el EVALUADOR (más
      // estricto que requiredToSaveWoo en variables: cada variación con precio
      // y stock) NO hay diálogo — se bloquea con su mensaje.
      const obligPend = resultadoFicha.reglas.filter((r) => r.aplicable && !r.pospuesta && r.nivel === 'obligatoria' && !r.cumplida);
      if (obligPend.length) {
        showToast({ message: `No se puede publicar. ${obligPend.map((r) => r.mensaje || r.nombre).join(' ')}`, variant: 'warning' });
        return;
      }
      // Tanda 6→7: el diálogo propio lista las RECOMENDADAS pendientes con su
      // detalle; "Completar la ficha" navega a la primera, "Publicar igual" publica.
      const pendientes = resultadoFicha.recomendadasPendientes;
      if (pendientes.length > 0) {
        setPublicarDlg({ pendientes });
        return;
      }
    }
    doWrite(estado);
  };

  // ── Validaciones en vivo (no bloquean; item 9) ──────────────────────────────
  const errs = useMemo(() => ({
    name: validateName(form.name).error,
    shortDescription: form.shortDescription ? validateShortDescription(form.shortDescription).error : null,
    sku: form.sku ? validateSku(form.sku).error : null,
    regularPrice: form.regularPrice ? validatePrice(form.regularPrice).error : null,
    salePrice: validateSalePrice(form.salePrice, form.regularPrice).error,
    weight: form.weight ? validateWeight(form.weight).error : null,
    dimensions: (form.length || form.width || form.height) ? validateDimensions({ length: form.length, width: form.width, height: form.height }).error : null,
    gtin: validateGtin(form.gtin).error,
  }), [form]);

  const skuDuplicado = form.sku && form.sku !== ownSku && skuExistsInCatalog(form.sku, existingSkus);

  // ── Tanda 6 (PARTE B): el evaluador corre EN VIVO sobre el form (sin red). ──
  const resultadoFicha = useMemo(() => evaluarFicha(
    entradaDesdeForm(form, vRows, {
      esNuevo: !isEdit && wooId == null,
      guiaOrigen: guiaInfo && guiaInfo.disponible !== false ? (guiaInfo.origen ?? null) : null,
    }),
    { tipos, categorias: categories, vitrinaActiva: catalogoConfig?.vitrinaActiva === true }
  ), [form, vRows, isEdit, wooId, guiaInfo, tipos, categories, catalogoConfig]);

  // Tanda 7 (PARTE C): llegada desde Inventario ("Crear variación") — al
  // terminar la carga salta a la tabla de variaciones; si el producto es
  // SIMPLE avisa que primero debe pasarse a variable (selector Tipo, General).
  const focoInicialHecho = useRef(false);
  useEffect(() => {
    if (!focoInicial || loading || focoInicialHecho.current) return;
    focoInicialHecho.current = true;
    if (focoInicial === 'variaciones') {
      if (formRef.current.type === 'variable') {
        irARegla({ tab: 'general', foco: 'variaciones' });
      } else {
        showToast({
          message: 'Este producto es simple: para crear variaciones, cámbialo a variable en el campo Tipo y guarda.',
          variant: 'warning',
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focoInicial, loading]);

  // Tanda 7b — ALTA de variación: primero el PADRE (el atributo propio y su
  // opción nueva tienen que existir para que Woo acepte la variación), después
  // el POST de la variación; si el POST falla, el padre se REVIERTE a sus
  // atributos originales (la opción no puede quedar publicada sin variación).
  const crearVariacion = useCallback(async ({ eje, option, ejesValores, sku, precio, stock, imageId }) => {
    const pid = wooIdRef.current ?? productId;
    const attrsViejos = attrsWooRef.current;
    // Tanda 7c: el modal muestra el eje CANÓNICO, pero el alta escribe contra
    // el atributo REAL del padre (puede seguir siendo "tonos" hasta el próximo
    // guardado) — si no, se crearía un segundo atributo con el nombre nuevo.
    const ejeRealDe = (nombre) => (attrsViejos.find((a) => a.variation && a.name === nombre) || {}).name || nombre;
    // Tanda 8: alta multi-eje (un valor por eje) → set completo en `ejes`. El
    // alta mono-eje sigue el shape legado (paridad).
    const multi = Array.isArray(ejesValores) && ejesValores.length > 1;
    let attrsNuevos = attrsViejos;
    let body;
    let etiqueta;
    if (multi) {
      // P2b tonos: enhebrar el id del atributo GLOBAL (id>0) en cada eje para que Woo
      // ligue la variación a la taxonomía (pa_tono/pa_color), no a un local attribute_<name>.
      const pares = ejesValores.map((ev) => {
        const nom = ejeRealDe(ev.name);
        const a = attrsViejos.find((x) => x.variation && x.name === nom);
        const par = { name: nom, option: ev.option };
        if (Number(a?.id) > 0) par.id = Number(a.id); // local no lleva id (regla dura)
        return par;
      });
      pares.forEach((p) => { attrsNuevos = atributosParaAlta(attrsNuevos, p.name, p.option); });
      body = { ejes: pares };
      etiqueta = pares.map((p) => p.option).join(' · ');
    } else {
      const ejeAttrPadre = attrsViejos.find((a) => a.variation) || {};
      const ejeReal = ejeAttrPadre.name || eje;
      attrsNuevos = atributosParaAlta(attrsViejos, ejeReal, option);
      // P2b tonos: id del atributo GLOBAL (id>0) → Woo liga a la taxonomía; local NO lleva id.
      body = { attributeName: ejeReal, option };
      if (Number(ejeAttrPadre.id) > 0) body.attributeId = Number(ejeAttrPadre.id);
      etiqueta = option;
    }
    const putPadre = (attrs) => saveProductToWoo(formRef.current, {
      productId: pid, status: wooStatusRef.current || 'draft',
      includeCategories: false, camposTocados: {}, attributes: attrs, tipos,
    });
    // P3-1: la marca va ANTES de la primera escritura — el SSE que estas
    // escrituras disparan llega mientras Woo todavía responde, y un debounce
    // que venza en ese hueco leería «no fue propia» y avisaría de más.
    marcarEscrituraPropia();
    await putPadre(attrsNuevos);
    try {
      if (sku) body.sku = sku;
      if (precio) body.regular_price = precio;
      if (stock !== '') body.stock_quantity = stock;
      if (imageId != null) body.imageId = imageId;
      await createVariation(pid, body);
    } catch (e) {
      try {
        await putPadre(attrsViejos);
      } catch (e2) {
        throw new Error(`${e.message} — y la reversión del padre también falló: ${e2.message}`);
      }
      throw e;
    }
    showToast({ message: `Variación "${etiqueta}" creada.`, variant: 'success' });
    // P3-1 (bug 1.1, el repisador medido en e2e A5): la relectura post-alta
    // FUSIONA — lo editado sin guardar se conserva, la variación nueva aparece.
    await recargar(pid, { conservarSucios: true });
  }, [productId, tipos, recargar, showToast, marcarEscrituraPropia]);

  // Clic en una regla → pestaña + foco en el campo (data-foco).
  const irARegla = useCallback((destino) => {
    if (!destino) return;
    setTab(destino.tab);
    setTimeout(() => {
      const el = document.querySelector(`[data-foco="${destino.foco}"]`);
      if (el) {
        // jsdom no implementa scrollIntoView: sin el guard, el throw se comía el focus
        if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        if (typeof el.focus === 'function') el.focus({ preventScroll: true });
        // Tanda 7 (PARTE B): resaltar el campo UN segundo para que el ojo lo encuentre.
        el.classList.add('pp-flash');
        setTimeout(() => el.classList.remove('pp-flash'), 1000);
      }
    }, 60);
  }, []);

  // Defecto 3 (3d→3e): "Abrirlo" al producto que ya tiene el SKU — ENLAZA el
  // borrador al existente (la ficha de edición lo ofrece como "Usar el
  // borrador") y abre esa ficha; el contenido tipeado no se pierde y el próximo
  // guardado es PUT, no otro POST. En 3e la misma acción vive también en el
  // aviso EN LÍNEA del campo SKU, no solo en el error del POST.
  const abrirProductoExistente = async (id) => {
    if (id == null || !onOpenProduct) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    if (dirtyRef.current || draftIdRef.current) {
      try { await saveDraft(formRef.current, { draftId: draftIdRef.current, wcProductId: id }); } catch { /* ignore */ }
    }
    setDirty(false); dirtyRef.current = false; // que el desmontaje no cree un huérfano
    onOpenProduct(id);
  };
  const errSkuDuplicado = saveError ? esErrorDeSkuDuplicado(saveError) : false;
  const idExistente = (errSkuDuplicado || skuDuplicado) && buscarIdPorSku
    ? buscarIdPorSku(String(form.sku || '').trim())
    : null;

  const parentCats = useMemo(() => categories.filter((c) => c.parent === 0), [categories]);

  // El tipo vigente del formulario (fallback al primero: nunca sin plantilla).
  const tipoActual = tipoActivo(tipos, form.productKind);
  // Tanda 8c (J9): la columna/campo "Contenido" aplica a tipos que declaran
  // aplicaContenido (fallback a arquetipo cosmético). Caso acordado: aunque el
  // tipo NO lo lleve, si el producto YA tiene contenido guardado (padre o alguna
  // variación) NO se oculta —quedaría invisible y desactivar el flag nunca borra
  // lo guardado en Woo—: se muestra editable con un aviso (contenidoAjeno).
  const tipoLlevaContenido = aplicaContenidoDeTipo(tipoActual);
  const hayContenidoGuardado = !!String(form.contenidoValor || '').trim()
    || vRows.some((r) => String(r.contenidoValor || '').trim());
  const muestraContenido = tipoLlevaContenido || hayContenidoGuardado;
  const contenidoAjeno = muestraContenido && !tipoLlevaContenido;
  const esVariable = form.type === 'variable';
  // P3-12: flags «Más opciones» del tipo activo (qué campos muestra el bloque de la ficha).
  const moTipo = useMemo(() => masOpcionesDeTipo(tipoActivo(tipos, form.productKind)), [tipos, form.productKind]);
  // P3-12 remate: la proporción de foto del tipo (4:5/1:1/3:4) — editor de encuadre, textos, tubería
  // y criterio de baja resolución la leen.
  const propFoto = useMemo(() => propFotoDeTipo(tipoActivo(tipos, form.productKind)), [tipos, form.productKind]);
  const propFotoRef = useRef(propFoto); propFotoRef.current = propFoto; // para los callbacks de preview (definidos antes)
  const mostrarMasOpciones = !esConsumible; // slug/variación por defecto siempre; los demás, según el tipo
  // P3-12 «Variación por defecto»: mapear fila↔default_attributes ([{name,option}]).
  const etiquetaFila = useCallback((f) => (f && f.opciones && f.opciones.length ? f.opciones.map((o) => o.option).join(' / ') : ((f && f.option) || String(f && f.id))), []);
  const varDefaultAttrs = useCallback((f) => (f && f.opciones && f.opciones.length
    ? f.opciones.map((o) => ({ name: o.name, option: o.option }))
    : (f && f.attributeName ? [{ name: f.attributeName, option: f.option }] : [])), []);
  const defaultVarId = useMemo(() => {
    const da = form.defaultAttributes || [];
    if (!da.length) return '';
    const hit = (vRows || []).find((f) => {
      const a = varDefaultAttrs(f);
      return a.length && a.length === da.length && a.every((x) => da.some((d) => String(d.option || '') === String(x.option)));
    });
    return hit ? hit.id : '';
  }, [form.defaultAttributes, vRows, varDefaultAttrs]);
  const setDefaultVariacion = useCallback((id) => {
    const f = (vRows || []).find((x) => String(x.id) === String(id));
    update({ defaultAttributes: f ? varDefaultAttrs(f) : [] });
  }, [vRows, varDefaultAttrs, update]);
  // Refs para doWrite (useCallback con deps acotadas): el tipo de «Más opciones» y la lista de ejes.
  const moTipoRef = useRef(moTipo); moTipoRef.current = moTipo;
  const ejesListaRef = useRef(ejesLista); ejesListaRef.current = ejesLista;
  // P3-3: el bloque «Código de barras» del PRODUCTO SIMPLE lo decide la casilla
  // aplicaGtin del tipo — mismo patrón que contenido: si el tipo no lo lleva pero
  // YA hay un valor guardado, no se oculta (desactivar el flag nunca borra el
  // dato); se muestra con un aviso. En variables el GTIN va por variación (la
  // tabla lo gestiona aparte, sin cambios en esta tanda).
  const tipoLlevaGtin = tipoActual?.aplicaGtin === true;
  const hayGtinGuardado = !!String(form.gtin || '').trim();
  const muestraGtinSimple = tipoLlevaGtin || hayGtinGuardado;
  const gtinAjeno = muestraGtinSimple && !tipoLlevaGtin;
  // P3-4 (follow-up de P3-3): la COLUMNA «Código de barras» de la tabla de
  // variaciones sigue la misma regla — aparece si el tipo lleva GTIN o si alguna
  // variación ya tiene un valor (con aviso). Es la versión variable del gateo
  // que P3-3 hizo en el producto simple.
  const hayGtinEnVariaciones = vRows.some((r) => String(r.gtin || '').trim());
  const muestraGtinVar = tipoLlevaGtin || hayGtinEnVariaciones;
  const gtinVarAjeno = muestraGtinVar && !tipoLlevaGtin;
  // Ítem 1 (4c): el título SEO por defecto usa el nombre CONFIGURADO de la tienda.
  const seoTitlePreview = form.seoTitle || (form.name ? `${form.name} | ${nombreTienda}` : '');
  const seoDescPreview = form.seoMetadesc || form.shortDescription;

  // Sensor de arrastre de la galería (ajuste 6): umbral de 6 px para que los
  // clics en los botones y el alt de cada tarjeta no inicien un arrastre.
  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const onGalleryDragEnd = useCallback(({ active, over }) => {
    if (!over || active.id === over.id) return;
    const list = formRef.current.images || [];
    const from = list.findIndex((i) => i.id === active.id);
    const to = list.findIndex((i) => i.id === over.id);
    if (from >= 0 && to >= 0) moverImagen(from, to);
  }, [moverImagen]);

  // P3-3: en una tienda Shopify el peso y las dimensiones no tienen destino
  // (Shopify no los modela como Woo), así que la pestaña Envío se OCULTA con una
  // nota en General. El resto de campos sin destino en Shopify (fechas de oferta,
  // relacionados) se tratan en la serie de escritura a Shopify (hoy la escritura
  // está bloqueada con 409). Si el usuario estaba en Envío y se oculta, cae a General.
  const esShopify = plataforma === 'shopify';
  const tabs = {
    tabs: [
      { id: 'general', label: 'General' },
      ...(esShopify ? [] : [{ id: 'inventario', label: 'Envío' }]),
      { id: 'organizacion', label: 'Categoría, marca y relacionados' },
      { id: 'seo', label: 'SEO' },
      { id: 'otras', label: 'Imágenes · Vitrina' },
      ...(esConsumible ? [{ id: 'comestible', label: 'Comestible' }] : []),
    ],
    active: (esShopify && tab === 'inventario') ? 'general' : tab,
    onChange: setTab,
  };

  // Tanda 4 (PARTE A): tabla de variaciones EDITABLE — los cambios se guardan
  // con el mismo "Guardar cambios" de la página; la fila tocada se marca.
  // Tanda 4b (ítems 3-6): tarjeta propia de ancho completo con grilla de
  // columnas definidas (referencia: tablas de líneas de Compras, cp-var-grid);
  // los avisos de cada fila van DEBAJO de la fila completa en una línea; en
  // <900 px cada variación se apila como tarjeta.
  // Crear variaciones nuevas desde atributos globales llega después de P2-tax.
  // Tanda 7b: el alta exige que el producto EXISTA en Woo (la variación se
  // crea allá); en uno nuevo sin guardar el botón lo dice, no desaparece.
  const pidParaAlta = wooId ?? productId;
  // Tanda 7c (punto 3): sin SKU de padre, los botones de generar SKU no se
  // apagan — avisan el MOTIVO y llevan (foco + scroll) al campo SKU del
  // producto, por el mismo mecanismo data-foco de las reglas de la ficha.
  const MOTIVO_SKU_PADRE = 'Primero pon el SKU del producto; el de cada variación se arma a partir de él.';
  const sinSkuPadre = !String(form.sku || '').trim();
  const avisarSkuPadre = () => {
    showToast({ message: MOTIVO_SKU_PADRE, variant: 'warning' });
    irARegla({ tab: 'general', foco: 'sku' });
  };
  const ejeExistente = (attrsWoo.find((a) => a.variation) || {}).name || (vRows[0] && vRows[0].attributeName) || null;
  // Tanda 7d: la ficha modela UN eje por variación; sobre un producto de DOS
  // ejes (59 de 106 al medirse, 16-sep-2026) editar la opción o crear una
  // variación pisaría el segundo eje en Woo. Hasta la tanda que los soporte,
  // esas operaciones quedan en solo lectura y la normalización 7c INACTIVA
  // (renombrar attributes[0] reescribiría cada variación a mono-eje).
  const multiEje = esMultiEje(attrsWoo);
  const nombresEjes = ejesDeVariacion(attrsWoo).map((a) => a.name);
  // Tanda 7c (punto 2): el eje leído de Woo se contrasta con la lista canónica.
  const listaEjes = ejesCanonicos || EJES_INICIALES;
  const ejeNorm = ejeExistente ? normalizarEje(ejeExistente, listaEjes) : null;
  ejeNormRef.current = ejeNorm && ejeNorm.corregido && !multiEje ? { de: ejeExistente, a: ejeNorm.nombre } : null;
  // Tanda 8 (P8): normalización canónica POR EJE. Cada eje del padre se contrasta
  // con la lista canónica; los corregibles se muestran y, al guardar, se
  // renombran en el padre Y en el set de cada variación. La inactivación de la
  // 7d se retira: con el set completo, renombrar el eje ya no pierde el otro.
  const ejesNorm = multiEje
    ? nombresEjes.map((n) => ({ de: n, norm: normalizarEje(n, listaEjes) }))
    : [];
  correccionesEjesRef.current = ejesNorm.filter((e) => e.norm.corregido).map((e) => ({ de: e.de, a: e.norm.nombre }));
  // Tanda 8: con más de un eje la columna «Variación» se parte en N columnas —
  // el grid inline las inserta donde iba la única (mono-eje: sin style, DOM
  // byte-idéntico a la 7d). Las demás columnas (SKU…Acciones) no se tocan.
  // Tanda 8c (J9): la columna Contenido (104px) solo va si el tipo la lleva.
  // P3-4: la columna GTIN (minmax(124px, .8fr)) va solo si muestraGtinVar.
  const gridVarStyle = multiEje
    ? { gridTemplateColumns: `88px ${'minmax(90px, .8fr) '.repeat(nombresEjes.length)}minmax(244px, 1.3fr) ${muestraGtinVar ? 'minmax(124px, .8fr) ' : ''}80px 80px 56px ${muestraContenido ? '104px ' : ''}36px` }
    : undefined;
  // mono-eje toma el grid del CSS; las variantes desktop lo achican por columna
  // ausente (sin contenido y/o sin gtin).
  const claseSinContenido = muestraContenido ? '' : ' pp-vgrid--sin-contenido';
  const claseSinGtin = muestraGtinVar ? '' : ' pp-vgrid--sin-gtin';
  // P3-7: filas en el ORDEN PÚBLICO del eje (el que ve el cliente). Solo para
  // mostrar/numerar; las operaciones siguen por f.id, nunca por el índice.
  const vRowsOrden = useMemo(() => ordenarFilasPorEje(vRows, ejesLista), [vRows, ejesLista]);
  const variacionesTabla = (
    <div className="pp-section pp-section--vars">
      <div className="pp-varlist-head">
        <h3>Variaciones ({vRows.length})</h3>
        <div className="pp-varlist-actions">
          {vRows.length > 0 && (
            <Button size="sm" onClick={sinSkuPadre ? avisarSkuPadre : generarSkuTodas}
              title={sinSkuPadre ? MOTIVO_SKU_PADRE : 'Arma cada SKU con el del producto más el nombre de la variación'}>
              Generar SKU de todas las variaciones
            </Button>
          )}
          {vRows.length > 0 && (
            <Button size="sm" variant="primary" onClick={() => setAltaVar(true)} disabled={pidParaAlta == null}
              title={pidParaAlta == null ? 'Guarda el producto primero: la variación se crea en WooCommerce' : undefined}>
              + Agregar variación
            </Button>
          )}
        </div>
      </div>
      {/* Tanda 7c (punto 2): el estado del eje se DECLARA — corrección
          pendiente (grafía) o eje fuera de la lista canónica (sin bloquear). */}
      {!multiEje && ejeNorm && ejeNorm.corregido && (
        <p className="pp-aviso-eje" role="alert">
          El eje "{ejeExistente}" se leerá como "{ejeNorm.nombre}": al guardar se corregirá el nombre en WooCommerce.
        </p>
      )}
      {!multiEje && ejeNorm && !ejeNorm.reconocido && (
        <p className="pp-aviso-eje pp-aviso-eje--warn" role="alert">
          Eje no reconocido: "{ejeExistente}". Agrégalo en Configuración o corrígelo.
        </p>
      )}
      {/* Tanda 8 (P8): normalización POR EJE (multi-eje). Un eje corregible se
          ANUNCIA siempre. Tanda 8b: si la escritura está DESACTIVADA (hasta J8),
          el aviso NO promete que al guardar se corrija — sería una mentira: el
          guardado deja el nombre como está. */}
      {multiEje && ejesNorm.filter((e) => e.norm.corregido).map((e) => (
        <p key={e.de} className="pp-aviso-eje" role="alert">
          {NORMALIZACION_NOMBRE_EJE_MULTIEJE
            ? `El eje "${e.de}" se leerá como "${e.norm.nombre}": al guardar se corregirá el nombre en WooCommerce.`
            : `El eje "${e.de}" no está en la forma canónica ("${e.norm.nombre}"). Por ahora se mantiene como está; renombrar el nombre del eje en WooCommerce llegará más adelante. Puedes editar valores y variaciones normalmente.`}
        </p>
      ))}
      {multiEje && ejesNorm.filter((e) => !e.norm.reconocido).map((e) => (
        <p key={e.de} className="pp-aviso-eje pp-aviso-eje--warn" role="alert">
          Eje no reconocido: "{e.de}". Agrégalo en Configuración o corrígelo.
        </p>
      ))}
      {/* P3-4: la columna GTIN se muestra aunque el tipo no la lleve porque hay
          valores guardados — se avisa (desactivar el flag nunca borra el dato). */}
      {gtinVarAjeno && vRows.length > 0 && (
        <p className="pp-aviso-eje" role="note">
          Este tipo de producto normalmente no lleva código de barras; la columna se muestra porque alguna variación ya tiene un valor.
        </p>
      )}
      {/* P3-7: las filas van en el orden que la tienda muestra al cliente. El
          orden se cambia en Configuración → Catálogo (es global por eje), no acá. */}
      {vRows.length >= 2 && (
        <p className="pp-vorden-hint" role="note">
          Las variaciones están numeradas en el orden que ve el cliente en la tienda.
          Para cambiarlo, ve a <strong>Configuración → Catálogo → «Orden de los valores de variación»</strong> (afecta a toda la tienda).
        </p>
      )}
      {vRows.length === 0 ? (
        <div className="pp-vars-vacio">
          <p className="pp-muted">Este producto variable no tiene variaciones cargadas.</p>
          <Button variant="primary" onClick={() => setAltaVar(true)} disabled={pidParaAlta == null}
            title={pidParaAlta == null ? 'Guarda el producto primero: la variación se crea en WooCommerce' : undefined}>
            Agregar la primera variación
          </Button>
          {pidParaAlta == null && <p className="pp-muted">Guarda el producto primero: la variación se crea en WooCommerce.</p>}
        </div>
      ) : (
        <div className="pp-vtabla" role="table" aria-label="Variaciones" data-foco="variaciones" tabIndex={-1}>
          <div className={`pp-vgrid pp-vthead${claseSinContenido}${claseSinGtin}`} role="row" style={gridVarStyle}>
            <span role="columnheader">Foto</span>
            {/* Tanda 8 (P1): una columna por eje con su nombre real; mono-eje
                conserva la columna única "Variación". */}
            {multiEje
              ? nombresEjes.map((n) => <span key={n} role="columnheader">{n}</span>)
              : <span role="columnheader">Variación</span>}
            <span role="columnheader">SKU</span>
            {/* Ítem 10: la ayuda vive acá como tooltip, no repetida por fila.
                P3-4: la columna aparece solo si el tipo lleva GTIN o si alguna
                variación ya tiene valor. */}
            {muestraGtinVar && (
              <span role="columnheader" className="pp-vth-gtin" title={AYUDA_GTIN}>
                Código de barras (GTIN/EAN) <Info size={12} aria-hidden="true" />
              </span>
            )}
            <span role="columnheader">Precio</span>
            <span role="columnheader">Rebajado</span>
            <span role="columnheader">Stock</span>
            {muestraContenido && <span role="columnheader">Contenido</span>}
            <span role="columnheader" aria-label="Acciones" />
          </div>
          {vRowsOrden.map((f, idx) => {
            const n = idx + 1;
            const crudo = variations.find((v) => v.id === f.id);
            const sucia = Object.keys(f.tocados).length > 0;
            // Defecto 1 (4b): el chequeo excluye la propia variación y el propio
            // producto — el catálogo refrescado trae de vuelta los SKUs recién
            // guardados y el aviso disparaba en cada fila contra sí misma.
            const skuDup = skuDuplicadoDeFila(f, vRows, existingSkus, ownSku);
            const errRebaja = validateSalePrice(f.salePrice, f.regularPrice).error;
            const errGtin = validateGtin(f.gtin).error;
            const renombrada = f.tocados.option && f.option !== f.optionOriginal;
            // Defecto 2 (4b): renombrar NO cambia el SKU solo (etiquetas/QR
            // impresos); si deriva del nombre viejo, se OFRECE el nuevo.
            const skuOferta = skuPropuestoAlRenombrar(f);
            const hayAvisos = renombrada || skuOferta || skuDup || errRebaja || errGtin;
            return (
              <div key={f.id} className={`pp-vfila${sucia ? ' pp-vfila--dirty' : ''}`} role="rowgroup">
                <div className={`pp-vgrid pp-vrow${claseSinContenido}${claseSinGtin}`} role="row" style={gridVarStyle}>
                  <div className="pp-vcell pp-vcell--foto" role="cell">
                    {/* P3-9a (remate): la celda muestra «Cargando…»/listo/error mientras
                        la foto se asocia; el nº de orden público (P3-7) va dentro. */}
                    <CeldaFotoVariacion
                      n={n} src={crudo?.image?.src} option={f.option}
                      estado={fotoVarEstado[f.id]?.estado || null}
                      motivo={fotoVarEstado[f.id]?.motivo}
                      sinImagen={!crudo?.image?.src}
                      onCambiar={() => setImgSel({ target: 'variation', variationId: f.id })}
                      onReintentar={() => { const est = fotoVarEstado[f.id]; if (est && est.imageId != null) asociarFotoVariacion(f.id, est.imageId, est.src); }}
                      onUsarPrincipal={() => usarPrincipalEnVariacion(f.id)}
                    />
                  </div>
                  {/* Tanda 8 (P1/P2): una celda EDITABLE por eje. El texto en
                      curso vive en draftEje; el commit (blur) renombra el valor
                      en todas las variaciones que lo comparten, con aviso previo.
                      Tanda 7c: el label usa el EJE REAL del producto. Mono-eje
                      conserva la celda única y su onChange directo (paridad). */}
                  {multiEje
                    ? (f.opciones || []).map((op) => {
                      const clave = claveEje(f.id, op.name);
                      return (
                        <div key={op.name} className="pp-vcell" role="cell" data-label={op.name}>
                          <input className="pp-input pp-vinput" aria-label={`${op.name} de la variación ${n}`}
                            value={draftEje[clave] != null ? draftEje[clave] : op.option}
                            onChange={(e) => setDraftEje((p) => ({ ...p, [clave]: e.target.value }))}
                            onBlur={(e) => commitRenombreEje(f, op.name, e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }} />
                        </div>
                      );
                    })
                    : (
                      <div className="pp-vcell" role="cell" data-label="Variación">
                        <input className="pp-input pp-vinput" aria-label={`${f.attributeName || 'Valor'} de la variación ${n}`}
                          value={f.option}
                          onChange={(e) => setVRowCampo(f.id, { option: e.target.value })} />
                      </div>
                    )}
                  <div className="pp-vcell" role="cell" data-label="SKU">
                    <div className="pp-vsku">
                      <input className="pp-input pp-vinput pp-vinput--sku" aria-label={`SKU de la variación ${n}`} value={f.sku}
                        onChange={(e) => setVRowCampo(f.id, { sku: e.target.value.toUpperCase() })} />
                      <button type="button" className="pp-viconbtn"
                        title={sinSkuPadre ? MOTIVO_SKU_PADRE : 'Generar el SKU desde el SKU del padre y el valor'}
                        aria-label={`Generar SKU de la variación ${n}`}
                        onClick={() => (sinSkuPadre ? avisarSkuPadre() : generarSkuFila(f.id))}>
                        <Wand2 size={15} aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                  {muestraGtinVar && (
                  <div className="pp-vcell" role="cell" data-label="Código de barras">
                    <input className="pp-input pp-vinput pp-vinput--sku" inputMode="numeric" aria-label={`Código de barras de la variación ${n}`}
                      value={f.gtin} onChange={(e) => setVRowCampo(f.id, { gtin: e.target.value })} />
                  </div>
                  )}
                  <div className="pp-vcell" role="cell" data-label="Precio">
                    <input className="pp-input pp-vinput" inputMode="numeric" aria-label={`Precio de la variación ${n}`}
                      value={f.regularPrice} onChange={(e) => setVRowCampo(f.id, { regularPrice: e.target.value })} />
                  </div>
                  <div className="pp-vcell" role="cell" data-label="Rebajado">
                    <input className="pp-input pp-vinput" inputMode="numeric" aria-label={`Precio rebajado de la variación ${n}`}
                      value={f.salePrice} onChange={(e) => setVRowCampo(f.id, { salePrice: e.target.value })} />
                  </div>
                  <div className="pp-vcell" role="cell" data-label="Stock">
                    <input className="pp-input pp-vinput" inputMode="numeric" aria-label={`Stock de la variación ${n}`}
                      value={f.stockQty} onChange={(e) => setVRowCampo(f.id, { stockQty: e.target.value })} />
                  </div>
                  {muestraContenido && (
                    <div className="pp-vcell" role="cell" data-label="Contenido">
                      <div className="pp-vcontenido pp-vcontenido--joined">
                        <input inputMode="decimal" aria-label={`Contenido de la variación ${n}`}
                          placeholder="hereda" value={f.contenidoValor}
                          onChange={(e) => setVRowCampo(f.id, { contenidoValor: e.target.value })} />
                        <select aria-label={`Unidad de la variación ${n}`} value={f.contenidoUnidad}
                          onChange={(e) => setVRowCampo(f.id, { contenidoUnidad: e.target.value })}>
                          <option value="g">g</option>
                          <option value="ml">ml</option>
                          <option value="fl-oz">fl-oz</option>
                        </select>
                      </div>
                    </div>
                  )}
                  <div className="pp-vcell pp-vcell--acciones" role="cell">
                    <button type="button" className="pp-viconbtn pp-viconbtn--danger" title="Eliminar variación"
                      aria-label={`Eliminar la variación ${n}`} onClick={() => eliminarVariacion(f)}>
                      <Trash2 size={15} aria-hidden="true" />
                    </button>
                  </div>
                </div>
                {/* Ítem 4: los avisos de la fila van debajo, en UNA línea. */}
                {hayAvisos && (
                  <div className="pp-vavisos" role="row">
                    {renombrada && <span className="pp-vaviso">Esto cambia el nombre del tono en la tienda.</span>}
                    {skuOferta && (
                      <span className="pp-vaviso">
                        ¿Actualizar el SKU a {skuOferta}?
                        <button type="button" className="pp-vlink" onClick={() => setVRowCampo(f.id, { sku: skuOferta })}>
                          Actualizar el SKU
                        </button>
                      </span>
                    )}
                    {skuDup && <span className="pp-verror">Ya existe un producto con este SKU en el catálogo.</span>}
                    {errRebaja && <span className="pp-verror">{errRebaja}</span>}
                    {errGtin && <span className="pp-verror">{errGtin}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const saveLabel = saveState === 'saving' ? 'Guardando…'
    : saveState === 'error' ? 'Error al guardar'
    : saveState === 'saved' && savedAt ? `Guardado a las ${hhmm(savedAt)}`
    : dirty ? 'Sin guardar'
    : 'Borrador local';

  return (
    <div className="page-product mobile-safe page-shell">
      <div className="pp-topbar">
        <button type="button" className="cp-back-btn" onClick={handleBack}>
          <ArrowLeft size={18} /> Productos
        </button>
        {/* Ajuste 6: menú secundario con "Eliminar producto", separado de Guardar,
            solo cuando el producto EXISTE en WooCommerce (edición o recién creado). */}
        {(isEdit || wooId) && (
          <div className="pp-more">
            <button type="button" className="pp-more-btn" aria-label="Más acciones"
              aria-haspopup="menu" aria-expanded={moreOpen} onClick={() => setMoreOpen((o) => !o)}>
              <MoreHorizontal size={18} />
            </button>
            {moreOpen && (
              <>
                <div className="pp-more-scrim" onClick={() => setMoreOpen(false)} />
                <div className="pp-more-menu" role="menu">
                  <button type="button" role="menuitem" className="pp-more-danger" onClick={handleEliminar}>
                    <Trash2 size={15} aria-hidden="true" /> Eliminar producto
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* P3-10 punto 2 (remate): la frescura + «Actualizar desde la tienda» van en el
          slot metadata (bajo el subtítulo), NO en las acciones — ahí apretaban la
          columna del copy y el chip se solapaba con el título. Agrupados (chip al lado
          del botón) envuelven juntos sin solaparse en ningún ancho. */}
      <PageHeader
        title={isEdit ? 'Editar producto' : 'Nuevo producto'}
        description={isEdit
          ? `${form.name || 'Producto'}${wooStatus ? ` · ${estadoWooLabel(wooStatus)} en WooCommerce` : ''}`
          : 'Carga el producto siguiendo las recomendaciones. Se guarda como borrador local mientras lo editas.'}
        tabs={tabs}
        metadata={isEdit ? (
          <div className="pp-frescura-bar">
            <button type="button" className="pp-btn-tienda" onClick={actualizarDesdeTienda} disabled={actualizandoTienda}>
              <RefreshCw size={13} aria-hidden="true" className={actualizandoTienda ? 'pp-spin' : ''} />
              {actualizandoTienda ? 'Actualizando…' : 'Actualizar desde la tienda'}
            </button>
            {frescura && (() => {
              const fr = textoFrescura(frescura);
              return fr ? (
                <span className={`pp-frescura${fr.alerta ? ' pp-frescura--alerta' : ''}`} role="status">
                  {fr.texto}
                </span>
              ) : null;
            })()}
          </div>
        ) : null}
        secondaryActions={<>
          <span className={`pp-savestate pp-savestate--${saveState}`}>
            <Clock size={13} aria-hidden="true" /> {saveLabel}
          </span>
          {/* Ajuste 9: selector de estado de WordPress junto a las acciones. */}
          <select className="pp-input pp-estado-sel" aria-label="Estado" value={estado}
            onChange={(e) => {
              const v = e.target.value;
              setEstado(v); registrarSucios(['estado']); marcarTocados({ estado: true });
              // P16: al activar «Programada» sin fecha previa, precargar la de ahora
              // (hora Bogotá); el usuario la ajusta. Salir de future NO borra el
              // valor (por si vuelve), pero deja de viajar.
              if (v === 'future' && !formRef.current.fechaProgramada) {
                setForm((p) => ({ ...p, fechaProgramada: colAhoraInputLocal() }));
              }
            }}>
            <option value="publish">Publicado</option>
            <option value="draft">Borrador</option>
            <option value="private">Privado (solo lo ven administradores)</option>
            <option value="pending">Pendiente de revisión</option>
            {/* P16 (Programados): status future con fecha de publicación. */}
            <option value="future">Programada</option>
          </select>
          {estado === 'future' && (
            <div className="pp-programar">
              <label className="pp-programar-lbl" htmlFor="pp-fecha-prog">Publicar el (hora de Colombia):</label>
              <input id="pp-fecha-prog" type="datetime-local" className="pp-input pp-programar-input"
                value={form.fechaProgramada || ''}
                onChange={(e) => { setForm((p) => ({ ...p, fechaProgramada: e.target.value })); registrarSucios(['estado']); marcarTocados({ estado: true }); }} />
              {form.fechaProgramada
                ? <span className="pp-programar-info">Programada para {fmtFechaHoraBogota(form.fechaProgramada)}. Para quitar la programación, elige otro estado.</span>
                : <span className="pp-programar-info pp-programar-info--warn">Elige la fecha y hora de publicación.</span>}
            </div>
          )}
          {/* 3e (ajuste 4): Eliminar visible junto a Descartar (además del menú
              de tres puntos), solo cuando el producto existe en WooCommerce. */}
          {(isEdit || wooId) && <Button onClick={handleEliminar}>Eliminar</Button>}
          <Button onClick={handleDescartar}>Descartar</Button>
        </>}
        primaryAction={(
          <Button variant="primary" {...propsSoloLectura(soloLectura)} onClick={guardar} loading={publishing}
            disabled={esConsumible && capComest && !capComest.puede}
            title={esConsumible && capComest && !capComest.puede ? capComest.motivo : undefined}>
            {(isEdit || wooId) ? 'Guardar cambios' : 'Guardar'}
          </Button>
        )}
      />

      {/* Defecto 1a: resultado del guardado, sin navegar solo. */}
      {wooSaved && (
        <div className="pp-savedbanner" role="status">
          <span>
            <CheckCircle2 size={16} aria-hidden="true" />
            Guardado en WooCommerce a las {hhmm(wooSaved.at)} · {estadoWooLabel(wooSaved.status)}
          </span>
          <div className="pp-savedbanner-actions">
            <Button size="sm" onClick={() => setWooSaved(null)}>Seguir editando</Button>
            <Button size="sm" variant="primary" onClick={irAlListado}>Ver catálogo</Button>
          </div>
        </div>
      )}
      {saveError && (errSkuDuplicado ? (
        <div className="pp-saveerror" role="alert">
          <span>
            Ya existe un producto con el SKU {String(form.sku || '').trim()}.
            {idExistente == null && ' Búscalo en el listado para editarlo.'}
            {' '}El borrador local se conserva.
          </span>
          {idExistente != null && <Button size="sm" onClick={() => abrirProductoExistente(idExistente)}>Abrirlo</Button>}
          {/* El error REAL de Woo (mensaje + código) acompaña al amigable, no lo
              reemplaza — lo fija el assert de 3b (productGuardarWoo 1a). */}
          <span className="pp-saveerror-code">Detalle de WooCommerce: {saveError.message}</span>
        </div>
      ) : (
        <div className="pp-saveerror" role="alert">
          No se pudo guardar en WooCommerce: {saveError.message}. El borrador local se conserva.
        </div>
      ))}

      {/* P3-1: una actualización EXTERNA llegó con cambios sin guardar — se
          fusionó conservándolos; el aviso lo dice y se descarta a mano. */}
      {avisoTienda && (
        <div className="pp-draftbanner" role="status">
          <span><Info size={15} aria-hidden="true" /> La tienda actualizó este producto; tus cambios sin guardar se conservan.</span>
          <div className="pp-draftbanner-actions">
            <Button size="sm" onClick={() => setAvisoTienda(false)}>Entendido</Button>
          </div>
        </div>
      )}

      {pendingDraft && (
        <div className="pp-draftbanner">
          <span><Info size={15} aria-hidden="true" /> Tienes un borrador local de este producto
            {pendingDraft.updatedAt ? ` del ${hhmm(pendingDraft.updatedAt)}` : ''}.</span>
          {/* 3e (defecto 2): "Usar el borrador" / "Ignorar" — no confundir con
              el Descartar del encabezado, que sí destruye. */}
          <div className="pp-draftbanner-actions">
            <Button size="sm" variant="primary" onClick={retomar}>Usar el borrador</Button>
            <Button size="sm" onClick={ignorarPendiente}>Ignorar</Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="status-msg">Cargando producto…</div>
      ) : (
        <>
        <div className="pp-layout">
        <div className="pp-main">
          {tab === 'general' && (
            <div className="pp-section">
              {/* P3-3: aviso corto en tiendas Shopify — algunos campos no se
                  sincronizan y la pestaña Envío (peso/dimensiones) se oculta. */}
              {esShopify && (
                <p className="pp-muted" role="note">
                  <Info size={14} aria-hidden="true" /> Esta tienda es Shopify: el peso y las dimensiones no se sincronizan (la pestaña Envío no aparece) y la edición de productos hacia Shopify todavía no está disponible.
                </p>
              )}
              {/* Item 5: tipo de producto al inicio, antes del nombre. Tanda 4c:
                  las opciones son los tipos CONFIGURADOS de la tienda. */}
              <Field label="Tipo de producto" help="Define la plantilla de descripción y qué campos aplican. Los tipos se editan en Configuración → Catálogo.">
                <div className="pp-radio">
                  {tipos.map((t) => (
                    <label key={t.id}>
                      <input type="radio" name="kind" checked={tipoActual?.id === t.id} onChange={() => update({ productKind: t.id })} />
                      {' '}{t.nombre}
                    </label>
                  ))}
                </div>
              </Field>

              <Field label="Nombre" required
                help={tipoActual?.marcaObligatoria ? 'Empieza con la marca y sigue con el nombre oficial del producto.' : 'Nombre propio del producto, con mayúscula inicial.'}
                example={tipoActual?.marcaObligatoria ? 'Ejemplo: Aura Rubor Compacto' : 'Ejemplo: Vestido Aurora'} error={errs.name}>
                <input className="pp-input" data-foco="name" value={form.name} onChange={(e) => update({ name: e.target.value })} />
              </Field>

              <Field label="Descripción corta" help="Una o dos frases de qué es (máx. 150)."
                example="Ejemplo: Suave, liviano y pensado para el uso de todos los días." error={errs.shortDescription}>
                <textarea className="pp-input" data-foco="shortDescription" rows={2} maxLength={200} value={form.shortDescription}
                  onChange={(e) => update({ shortDescription: e.target.value })} />
                <span className="pp-count">{form.shortDescription.length}/150</span>
              </Field>

              <div className="pp-subhead">Descripción larga</div>
              {descMode === 'plano' ? (
                <>
                  <Field label="Descripción larga" help="Esta descripción tiene un formato propio; se conserva tal cual salvo que la edites aquí.">
                    <textarea className="pp-input" rows={6} value={descPlano} onChange={(e) => updatePlano(e.target.value)} />
                  </Field>
                  <Button size="sm" onClick={() => { setDescMode('guiada'); updateGuiada({ descParrafo: descPlano, descDetalles: '', descCierre: '' }); }}>
                    Reescribir con la plantilla
                  </Button>
                </>
              ) : (
                <>
                  <Field label="Párrafo" help="2–4 líneas: qué es, para qué sirve, qué lo hace especial.">
                    <AutoTextarea data-foco="descParrafo" value={form.descParrafo} onChange={(e) => updateGuiada({ descParrafo: e.target.value })} />
                  </Field>
                  <Field label="Detalles (uno por línea)" help="Un detalle por línea. Usa los sugeridos como punto de partida.">
                    <AutoTextarea value={form.descDetalles}
                      placeholder={`Ejemplo:\n${(tipoActual?.detallesSugeridos || []).join('\n')}`}
                      onChange={(e) => updateGuiada({ descDetalles: e.target.value })} />
                    <button type="button" className="pp-suggest-btn"
                      onClick={() => updateGuiada({ descDetalles: [form.descDetalles.trim(), (tipoActual?.detallesSugeridos || []).join('\n')].filter(Boolean).join('\n') })}>
                      Usar sugeridos
                    </button>
                  </Field>
                  <Field label={tipoActual?.cierreTitulo || 'Cierre'} help="2–3 recomendaciones.">
                    <AutoTextarea value={form.descCierre} minRows={2} onChange={(e) => updateGuiada({ descCierre: e.target.value })} />
                  </Field>
                  {descriptionToText(form.description) && (
                    <div className="pp-descprev">
                      <span className="pp-descprev-h">Vista previa</span>
                      {descriptionToText(form.description).split('\n').map((l, i) => <p key={i}>{l}</p>)}
                    </div>
                  )}
                </>
              )}

              <div className="pp-row">
                <Field label="Tipo" help="Variable si tiene tonos, colores o tallas.">
                  <select className="pp-input" value={form.type} onChange={(e) => update({ type: e.target.value })}>
                    <option value="simple">Producto simple</option>
                    <option value="variable">Producto variable</option>
                  </select>
                </Field>
              </div>

              {/* Ajuste 8: Precio y Cantidad comparten fila (media anchura cada
                  uno). En variables ninguno existe en el padre: se editan en la
                  tabla de variaciones (tanda 4). */}
              {esVariable ? (
                // Tanda 4: la ayuda del padre LEE la tabla. Tanda 4c (ítem 3):
                // el dato real va con FORMATO DE DATO (moneda, unidades y su
                // propio estilo), separado de la frase de ayuda.
                <>
                  <p className="pp-muted"><Info size={14} aria-hidden="true" />
                    {' '}El precio y la cantidad se editan en la tabla de variaciones.
                  </p>
                  <p className="pp-vardato">{resumenPadreVariable(vRows)}</p>
                </>
              ) : (
                <>
                  {/* P3-2: precio, cantidad y precio rebajado — tres campos
                      cortos en una sola fila (antes el rebajado iba solo y se
                      estiraba a todo el ancho). */}
                  <div className="pp-row">
                    <Field label="Precio normal" required help="En pesos, sin puntos." example="Bien: 155000 · Mal: 155.000" error={errs.regularPrice}>
                      <input className="pp-input" data-foco="regularPrice" inputMode="numeric" value={form.regularPrice} onChange={(e) => update({ regularPrice: e.target.value })} />
                    </Field>
                    {/* Ajuste 5 (3c): el seguimiento de stock ya no es opcional —
                        manage_stock viaja SIEMPRE true (como backorders). La
                        Cantidad se muestra siempre. */}
                    <Field label="Cantidad" required help="Stock real." error={null}>
                      <input className="pp-input" data-foco="stockQty" inputMode="numeric" value={form.stockQty} onChange={(e) => update({ stockQty: e.target.value })} />
                    </Field>
                    <Field label="Precio rebajado" help="Solo si está en oferta; menor al normal." error={errs.salePrice}>
                      <input className="pp-input" inputMode="numeric" value={form.salePrice} onChange={(e) => update({ salePrice: e.target.value })} />
                    </Field>
                  </div>
                  {form.salePrice && (
                    <div className="pp-row">
                      <Field label="Oferta desde"><input type="date" className="pp-input" value={form.saleFrom} onChange={(e) => update({ saleFrom: e.target.value })} /></Field>
                      <Field label="Oferta hasta"><input type="date" className="pp-input" value={form.saleTo} onChange={(e) => update({ saleTo: e.target.value })} /></Field>
                    </div>
                  )}
                </>
              )}

              {/* Reorg 2c: SKU y GTIN en General, después de Precio */}
              <div className="pp-row">
                <Field label="SKU"
                  help={`${tipoActual?.marcaObligatoria ? 'MARCA-PRODUCTO.' : 'Prefijo de la tienda + nombre.'} Solo mayúsculas, números y guiones.`}
                  example={tipoActual?.marcaObligatoria ? 'Ejemplo: AURA-RUBORCOMP' : `Ejemplo: ${prefijoSku ? `${prefijoSku}-` : ''}VESTIDO-AURORA`}
                  error={errs.sku || (skuDuplicado ? (
                    // Defecto 3 (3e): el aviso en línea también trae "Abrirlo",
                    // no solo el error del POST.
                    <>
                      Ya existe un producto con este SKU en el catálogo.
                      {idExistente != null && (
                        <button type="button" className="pp-error-link" onClick={() => abrirProductoExistente(idExistente)}>Abrirlo</button>
                      )}
                    </>
                  ) : null)}>
                  <div className="pp-sku-row">
                    <input className="pp-input" data-foco="sku" value={form.sku} onChange={(e) => update({ sku: e.target.value.toUpperCase() })} />
                    <Button size="sm" onClick={() => update({ sku: generateSku(form, { tipos, prefijoPropio: prefijoSku }) })} disabled={!form.name.trim()}>Generar</Button>
                  </div>
                </Field>
                {/* Tanda 4b (ítems 9-10): en variable el código de barras se
                    pone POR VARIACIÓN (como precio y cantidad); en simple, acá.
                    P3-3: en simple el bloque solo aparece si el tipo lleva GTIN
                    (o si ya hay un valor guardado, con aviso). */}
                {(esVariable || muestraGtinSimple) && (
                <Field label="Código de barras (GTIN/EAN)"
                  help={esVariable ? null : `${AYUDA_GTIN}${gtinAjeno ? ` Este tipo de producto normalmente no lo lleva; se muestra porque ya tiene un valor.` : ''}`}
                  error={esVariable ? null : errs.gtin}>
                  {esVariable ? (
                    <p className="pp-muted"><Info size={14} aria-hidden="true" /> Se pone por variación, en la tabla de variaciones.</p>
                  ) : (
                    <input className="pp-input" data-foco="gtin" inputMode="numeric" value={form.gtin} onChange={(e) => update({ gtin: e.target.value })} />
                  )}
                </Field>
                )}
              </div>

              {/* Tanda 4: contenido del PADRE (meta _vitrina_contenido). Las
                  variaciones sin contenido propio heredan este.
                  Tanda 8c (J9): solo en tipos que llevan contenido (mismo criterio
                  que la columna de variaciones), o si hay datos guardados. */}
              {contenidoAjeno && (
                <p className="pp-aviso-eje" role="note">
                  Este tipo de producto no usa contenido (ml/g), pero hay valores guardados.
                  Se muestran para que no queden ocultos; cambiar el tipo no los borra.
                </p>
              )}
              {muestraContenido && (
              <div className="pp-row">
                <Field label="Contenido" help="Cantidad del producto (30 ml, 50 g). Las variaciones sin contenido propio heredan este.">
                  <div className="pp-vcontenido">
                    <input className="pp-input pp-vnum" data-foco="contenido" inputMode="decimal" aria-label="Contenido del producto"
                      value={form.contenidoValor} onChange={(e) => update({ contenidoValor: e.target.value })} />
                    <select className="pp-input pp-vunidad" aria-label="Unidad del contenido del producto"
                      value={form.contenidoUnidad} onChange={(e) => update({ contenidoUnidad: e.target.value })}>
                      <option value="g">g</option>
                      <option value="ml">ml</option>
                      <option value="fl-oz">fl-oz</option>
                    </select>
                  </div>
                </Field>
              </div>
              )}

              {/* Tanda 5 (PARTE B): guía de tallas — solo si el tipo de producto
                  la aplica (Configuración → Catálogo). El origen lo dice el
                  árbitro del theme; las acciones escriben _vitrina_guia_id /
                  _vitrina_medidas con la regla de solo tocados. */}
              {(tipoActual?.aplicaTallas) && (
                <>
                  <div className="pp-subhead">Guía de tallas</div>
                  {(!isEdit && wooId == null) ? (
                    <p className="pp-muted"><Info size={14} aria-hidden="true" /> La guía se asigna después de guardar.</p>
                  ) : guiaInfo == null ? (
                    <p className="pp-muted">Consultando la guía de tallas…</p>
                  ) : guiaInfo.disponible === false ? (
                    <p className="pp-muted"><Info size={14} aria-hidden="true" /> {guiaInfo.motivo || 'Disponible cuando el tema Vitrina esté activo en la tienda'}</p>
                  ) : (
                    <div className="pp-guia" data-foco="guia" tabIndex={-1}>
                      <p className="pp-guia-origen">
                        {textoDeOrigen(guiaInfo)}
                        {(guiaInfo.origen === 'categoria' || guiaInfo.origen === 'producto') && onIrAGuias && (
                          <button type="button" className="pp-vlink" onClick={onIrAGuias}>Ver la guía</button>
                        )}
                        {guiaInfo.origen === 'ninguna' && onIrAGuias && (
                          <button type="button" className="pp-vlink" onClick={onIrAGuias}>Asignar una guía a la categoría</button>
                        )}
                      </p>
                      {(camposTocados.guiaId || camposTocados.medidas) && (
                        <p className="pp-vaviso">El cambio de guía se aplica al guardar.</p>
                      )}
                      <div className="pp-guia-acciones">
                        <select className="pp-input pp-guia-select" aria-label="Usar otra guía" value=""
                          onFocus={cargarGuiasParaElegir} onMouseDown={cargarGuiasParaElegir}
                          onChange={(e) => { if (e.target.value) { update({ guiaId: e.target.value }); setMedidasAbierta(false); } }}>
                          <option value="">Usar otra guía…</option>
                          {(guiasParaElegir || []).map((g) => (
                            <option key={g.id} value={g.id}>{g.titulo}</option>
                          ))}
                        </select>
                        {(guiaInfo.origen === 'producto' || camposTocados.guiaId) && (
                          <button type="button" className="pp-vlink" onClick={() => { update({ guiaId: '' }); setMedidasAbierta(false); }}>
                            Volver a la de la categoría
                          </button>
                        )}
                        {!medidasAbierta && guiaInfo.origen !== 'propia' && form.medidas == null && (
                          <button type="button" className="pp-vlink" onClick={() => {
                            setMedidasAbierta(true);
                            update({ medidas: guiaInfo.tabla || tablaVacia() });
                          }}>
                            Esta prenda tiene medidas distintas
                          </button>
                        )}
                        {!medidasAbierta && (guiaInfo.origen === 'propia' || form.medidas != null) && (
                          <button type="button" className="pp-vlink" onClick={() => {
                            setMedidasAbierta(true);
                            if (form.medidas == null) update({ medidas: guiaInfo.tabla || tablaVacia() });
                          }}>
                            Editar la tabla propia
                          </button>
                        )}
                        {(guiaInfo.origen === 'propia' || form.medidas != null) && (
                          <button type="button" className="pp-vlink pp-vlink--danger" onClick={() => {
                            setMedidasAbierta(false);
                            update({ medidas: null });
                          }}>
                            Quitar tabla propia
                          </button>
                        )}
                      </div>
                      {medidasAbierta && form.medidas != null && (
                        <div className="pp-guia-medidas">
                          <TablaMedidasEditor tabla={form.medidas} onChange={(t) => update({ medidas: t })} rotulo="tabla propia" />
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

            </div>
          )}

          {tab === 'inventario' && (
            <div className="pp-section">
              {/* P3-2: Peso y Dimensiones lado a lado (ambos son datos cortos). */}
              <div className="pp-row">
                <Field label="Peso (kg)" help="No dejes 0: las transportadoras cotizan con eso." example="Bien: 0.3 · Mal: 0" error={errs.weight}>
                  <input className="pp-input" data-foco="weight" inputMode="decimal" value={form.weight} onChange={(e) => update({ weight: e.target.value })} />
                </Field>
                <Field label="Dimensiones (cm)" help="Largo × ancho × alto reales del paquete." error={errs.dimensions}>
                  <div className="pp-dims">
                    <input className="pp-input" placeholder="Largo" inputMode="decimal" value={form.length} onChange={(e) => update({ length: e.target.value })} />
                    <input className="pp-input" placeholder="Ancho" inputMode="decimal" value={form.width} onChange={(e) => update({ width: e.target.value })} />
                    <input className="pp-input" placeholder="Alto" inputMode="decimal" value={form.height} onChange={(e) => update({ height: e.target.value })} />
                  </div>
                </Field>
              </div>
            </div>
          )}

          {tab === 'organizacion' && (
            <div className="pp-section">
              {/* Item 1: árbol con casillas que refleja TODAS las categorías actuales. */}
              <Field label="Categorías" help="Marca la categoría más específica, no solo la general. El árbol se simplifica en P2-tax.">
                <div className="pp-cattree" data-foco="categorias" tabIndex={-1}>
                  {parentCats.length === 0 && <p className="pp-muted">Cargando categorías…</p>}
                  {parentCats.map((parent) => {
                    const hijos = categories.filter((c) => c.parent === parent.id);
                    return (
                      <div key={parent.id} className="pp-catgroup">
                        <label className="pp-catnode">
                          <input type="checkbox" checked={form.categoryIds.includes(parent.id)} onChange={() => toggleCategoria(parent.id)} />
                          {parent.name}
                        </label>
                        {hijos.map((h) => (
                          <label key={h.id} className="pp-catnode pp-catchild">
                            <input type="checkbox" checked={form.categoryIds.includes(h.id)} onChange={() => toggleCategoria(h.id)} />
                            {h.name}
                          </label>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </Field>
              {/* P3-4: marca (una, taxonomía brands) y colecciones (varias,
                  tags coleccion-*). Se crean/renombran en Catálogo → Marcas /
                  Colecciones. */}
              <Field label="Marca" help={tipoActual?.marcaObligatoria ? `Obligatoria para ${tipoActual.nombre}. La marca del producto.` : 'Opcional. La marca del producto.'}>
                <select className="pp-input" data-foco="marca" value={form.brandId} onChange={(e) => update({ brandId: e.target.value })}>
                  <option value="">(Sin marca)</option>
                  {marcasLista.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </Field>
              <Field label="Colección"
                help={`${tipoActual?.pideColeccion ? 'La ficha señala la colección como pendiente si falta. ' : ''}Un producto puede estar en varias colecciones.`}>
                {coleccionesLista.length === 0 ? (
                  <p className="pp-muted">No hay colecciones todavía. Créalas en Catálogo → Colecciones.</p>
                ) : (
                  <div className="pp-coltree" data-foco="colecciones" tabIndex={-1}>
                    {coleccionesLista.map((c) => (
                      <label key={c.id} className="pp-catnode">
                        <input type="checkbox" checked={form.collectionTagIds.includes(c.id)}
                          onChange={() => {
                            const set = new Set(form.collectionTagIds);
                            if (set.has(c.id)) set.delete(c.id); else set.add(c.id);
                            update({ collectionTagIds: [...set] });
                          }} />
                        {c.nombre}
                      </label>
                    ))}
                  </div>
                )}
              </Field>
              {/* Tanda 7 (PARTE A): "Combínalo con" — hasta 3 elegidos, fila de
                  sugeridos (solo sugerencias, nada viaja sin elegirlo). */}
              <div className="pp-subhead">Productos relacionados</div>
              <ProductosRelacionados
                value={form.crossSellIds}
                onChange={(ids) => update({ crossSellIds: ids })}
                productos={productosCatalogo}
                productoId={wooId ?? productId}
                categorias={categories}
                categoryIds={form.categoryIds}
              />
            </div>
          )}

          {tab === 'seo' && (
            <div className="pp-section">
              <Field label="Título SEO" help="Palabra importante al inicio, tienda al final." example={`Ejemplo: Nombre del producto en Colombia | ${nombreTienda}`}>
                <input className="pp-input" data-foco="seoTitle" value={form.seoTitle} onChange={(e) => update({ seoTitle: e.target.value })} />
              </Field>
              <Field label="Meta description" help="Máx. 150. Si la dejas vacía, Google usa la descripción corta.">
                <textarea className="pp-input" rows={2} maxLength={200} value={form.seoMetadesc} onChange={(e) => update({ seoMetadesc: e.target.value })} />
              </Field>
              <div className="pp-subhead">Vista previa en Google</div>
              <div className="pp-google">
                {/* Vista previa SIMULADA: el dominio es un marcador genérico (la
                    config no conoce el dominio de la tienda), no el real. */}
                <div className="pp-google-url">{slugify(nombreTienda) || 'mi-tienda'}.com › producto › {slugify(form.name) || 'nombre-del-producto'}</div>
                <div className="pp-google-title">{seoTitlePreview || `Título del producto | ${nombreTienda}`}</div>
                <div className="pp-google-desc">{seoDescPreview || 'La descripción corta aparecerá aquí como texto del resultado.'}</div>
              </div>
            </div>
          )}

          {/* P3-12: bloque plegado «Más opciones» — muestra SOLO lo que el tipo activó (+ enlace y
              variación por defecto, siempre). Los campos viajan solo si se tocan (regla del status). */}
          {tab === 'general' && mostrarMasOpciones && (
            <details className="pp-section pp-masopciones">
              <summary className="pp-masopciones-sum">Más opciones</summary>
              <div className="pp-masopciones-body">
                {moTipo.destacado && (
                  <label className="pp-masopciones-check">
                    <input type="checkbox" checked={!!form.featured} onChange={(e) => update({ featured: e.target.checked })} /> Producto destacado
                  </label>
                )}
                {moTipo.visibilidad && (
                  <div className="pp-field">
                    <label>Visibilidad en el catálogo</label>
                    <select value={form.catalogVisibility || 'visible'} aria-label="Visibilidad en el catálogo"
                      onChange={(e) => update({ catalogVisibility: e.target.value })}>
                      {VISIBILIDADES.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
                    </select>
                    <small>Oculta = se compra solo por enlace.</small>
                  </div>
                )}
                {moTipo.oferta && (
                  <div className="pp-field">
                    <label>Oferta programada (hora Bogotá)</label>
                    <input type="datetime-local" value={form.ofertaDesde || ''} aria-label="Oferta desde"
                      onChange={(e) => update({ ofertaDesde: e.target.value, oferta: true })} />
                    <input type="datetime-local" value={form.ofertaHasta || ''} aria-label="Oferta hasta"
                      onChange={(e) => update({ ofertaHasta: e.target.value, oferta: true })} />
                    {esVariable && <small>Se aplica a todas las variaciones con precio rebajado.{form.ofertaMixta ? ' ⚠️ Hoy algunas variaciones tienen fechas distintas; al guardar se unifican.' : ''}</small>}
                  </div>
                )}
                {moTipo.reservas && (
                  <div className="pp-field">
                    <label>Reservas (comprar sin stock){esVariable ? ' — se aplica a todas las variaciones' : ''}</label>
                    <select value={form.backorders || 'no'} aria-label="Reservas (comprar sin stock)"
                      onChange={(e) => update({ backorders: e.target.value })}>
                      {RESERVAS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                    </select>
                  </div>
                )}
                {moTipo.umbral && (
                  <div className="pp-field">
                    <label>Umbral de stock bajo{esVariable ? ' (todas las variaciones)' : ''}</label>
                    <input type="number" min={0} value={form.lowStockAmount ?? ''} aria-label="Umbral de stock bajo"
                      placeholder={moTipo.umbralDefault != null ? String(moTipo.umbralDefault) : ''}
                      onChange={(e) => update({ lowStockAmount: e.target.value.replace(/[^0-9]/g, '') })} />
                  </div>
                )}
                {esVariable && (
                  <div className="pp-field">
                    <label>Variación por defecto</label>
                    <select value={defaultVarId} aria-label="Variación por defecto"
                      onChange={(e) => setDefaultVariacion(e.target.value)}>
                      <option value="">La primera combinación</option>
                      {vRows.map((fv) => <option key={fv.id} value={fv.id}>{etiquetaFila(fv)}</option>)}
                    </select>
                  </div>
                )}
                <div className="pp-field">
                  <label>Enlace (slug)</label>
                  <input value={form.slug || ''} aria-label="Enlace (slug)" onChange={(e) => update({ slug: e.target.value })} />
                  {wooStatus === 'publish' && <small>⚠️ El producto está publicado: cambiar el enlace rompe los enlaces que ya circulan.</small>}
                </div>
              </div>
            </details>
          )}

          {tab === 'comestible' && esConsumible && (
            <div className="pp-section">
              <SeccionComestible
                comestible={comestible}
                onChange={setComestible}
                errores={erroresComest}
                nombre={form.name}
                aviso={capComest && !capComest.puede ? capComest.motivo : null}
              />
            </div>
          )}

          {tab === 'otras' && (
            <div className="pp-section">
              {/* Item 19: imagen principal + galería + alt por imagen + imagen por variación */}
              <div className="pp-subhead pp-subhead--acciones">
                <span>Imagen principal y galería</span>
                {/* P3-9a: baja todas las fotos (galería + variaciones) en un .zip
                    con nombres SEO, sin recomprimir. Solo en edición y con fotos. */}
                {isEdit && form.images.length > 0 && (
                  <Button size="sm" onClick={descargarFotos} disabled={descargandoFotos}
                    title="Descarga todas las fotos (galería y variaciones) en un .zip con nombres SEO">
                    <Download size={14} aria-hidden="true" /> {descargandoFotos ? 'Descargando…' : 'Descargar fotos'}
                  </Button>
                )}
                {/* P3-9b: completar los textos alternativos vacíos (solo si hay). */}
                {isEdit && sinAltCount > 0 && (
                  <Button size="sm" onClick={completarAlts} disabled={completandoAlt}
                    title="Pone el nombre del producto (o nombre + color) como texto alternativo en las fotos que no tienen uno">
                    {completandoAlt ? 'Completando…' : `Completar textos alternativos (${sinAltCount})`}
                  </Button>
                )}
              </div>
              <p className="pp-help">Proporción {propFoto.label}. Principal + 2 a 6 fotos.</p>
              {/* Ajuste 7: el hint de reordenar va destacado con icono, no como ayuda gris. */}
              {form.images.length > 0 && (
                <p className="pp-reorder-hint">
                  <GripVertical size={16} aria-hidden="true" /> Arrastra para reordenar; la primera es la principal.
                </p>
              )}
              <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={onGalleryDragEnd}>
                <SortableContext items={form.images.map((i) => i.id)} strategy={rectSortingStrategy}>
                  <div className="pp-gallery" data-foco="galeria" tabIndex={-1}>
                    {form.images.map((img, idx) => (
                      <SortableFoto key={img.id} img={img} idx={idx} nombreProducto={form.name}
                        distintivo={distintivoFoto(prevPorUrl[img.src], img.optimizada, propFoto)}
                        onHacerPrincipal={hacerPrincipal} onQuitar={quitarImagen} onAlt={editarAltImagen} />
                    ))}
                    <button type="button" className="pp-gadd" onClick={() => setImgSel({ target: form.images.length ? 'gallery' : 'main' })}>
                      <Info size={18} aria-hidden="true" /> Agregar imagen
                    </button>
                  </div>
                </SortableContext>
              </DndContext>

              {esVariable && (
                <>
                  <div className="pp-subhead">Imagen por variación</div>
                  <p className="pp-help">Cada variación debería tener su propia imagen.</p>
                  <div className="pp-varimgs">
                    {variations.map((v) => (
                      <FotoVariacionCard key={v.id} etiqueta={varLabel(v)} src={v.image?.src} alt={varLabel(v)}
                        distintivo={v.image?.src ? distintivoFoto(prevPorUrl[v.image.src], v.image.optimizada, propFoto) : null}
                        onCambiar={() => setImgSel({ target: 'variation', variationId: v.id })}
                        onUsarPrincipal={() => usarPrincipalEnVariacion(v.id)} />
                    ))}
                  </div>
                </>
              )}

              {/* P3-9b: histórico de optimización del producto (desde FotoOptimizada):
                  tarjeta blanca; una corrida por bloque (más reciente arriba) con una
                  línea por foto (miniatura + etiqueta) y acciones por foto y por corrida. */}
              {isEdit && optHistorial.corridas.length > 0 && (
                <div className="pp-opt-hist">
                  <div className="pp-opt-hist-head">
                    <HistorialOptToggle
                      abierto={optHistorialAbierto}
                      resumen={`${optHistorial.total.fotos} foto${optHistorial.total.fotos === 1 ? '' : 's'}, ${pesoLegible(optHistorial.total.ahorro)} ahorrados`}
                      onToggle={() => setOptHistorialAbierto((o) => !o)}
                    />
                    {/* «Revertir…» visible sin desplegar: revierte la corrida más reciente. */}
                    {!optHistorialAbierto && optHistorial.corridas[0] && !optHistorial.corridas[0].viejaBorrada && optHistorial.corridas[0].loteId && (
                      <button type="button" className="pp-opt-hist-revlink" onClick={() => revertir(optHistorial.corridas[0].loteId)}>Revertir…</button>
                    )}
                  </div>
                  {optHistorialAbierto && (
                    <>
                      {optHistorial.corridas.map((c, i) => (
                        <div className="pp-opt-corrida" key={c.loteId || i}>
                          <div className="pp-opt-corrida-head">
                            <span className="pp-opt-historial-fecha">{c.fecha ? fmtFechaHoraBogota(c.fecha) : '—'}</span>
                            <span>{c.fotos} foto{c.fotos === 1 ? '' : 's'} · {pesoLegible(c.ahorro)} ahorrados{c.errores > 0 ? <span className="pp-warn"> · {c.errores} con error</span> : ''}</span>
                            {c.fotos > 0 && c.loteId && (c.viejaBorrada
                              ? <span className="pp-opt-historial-nota">originales borradas</span>
                              : <span className="pp-opt-historial-acc">
                                <button type="button" onClick={() => revertir(c.loteId)}>Revertir todo</button>
                                <button type="button" onClick={() => borrarOriginales(c.loteId)}>Borrar originales</button>
                              </span>)}
                          </div>
                          <ul className="pp-opt-fotos">
                            {(c.detalle || []).map((f) => (
                              <li key={f.id}>
                                {f.thumb ? <img className="pp-opt-foto-thumb" src={f.thumb} alt="" loading="lazy" /> : <span className="pp-opt-foto-thumb pp-opt-foto-thumb--none" />}
                                <span className="pp-opt-foto-et">{f.etiqueta}</span>
                                <span className="pp-opt-foto-peso">{pesoLegible(f.de)} → {pesoLegible(f.a)}</span>
                                {f.id && (f.viejaBorrada
                                  ? <span className="pp-opt-historial-nota">original borrada</span>
                                  : <span className="pp-opt-historial-acc">
                                    <button type="button" onClick={() => revertir(`_${f.id}`, true)}>Revertir</button>
                                    <button type="button" onClick={() => borrarOriginales(`_${f.id}`, true)}>Borrar original</button>
                                  </span>)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                      <p className="pp-opt-historial-total">
                        En total: {optHistorial.total.fotos} foto{optHistorial.total.fotos === 1 ? '' : 's'}, {pesoLegible(optHistorial.total.ahorro)} ahorrados ({pesoLegible(optHistorial.total.de)} → {pesoLegible(optHistorial.total.a)})
                        {optHistorial.total.errores > 0 && <span className="pp-warn"> · {optHistorial.total.errores} con error</span>}
                      </p>
                    </>
                  )}
                </div>
              )}
              {/* P3-9b (vista previa): mide cuánto pesaría cada foto reprocesada.
                  NO sube ni cambia nada — el «optimizar» (escritura) es la tanda
                  siguiente. Solo en edición y con fotos. */}
              {isEdit && form.images.length > 0 && (
                <>
                  <div className="pp-subhead pp-subhead--acciones">
                    <span>Optimizar fotos (vista previa)</span>
                    <Button size="sm" onClick={correrPreviewFotos} disabled={prevEstado === 'corriendo'}
                      title="Calcula cuánto pesaría cada foto reprocesada. No cambia nada todavía.">
                      <Gauge size={14} aria-hidden="true" /> {prevEstado === 'corriendo' ? `Calculando ${prevProgreso.hecho}/${prevProgreso.total}…` : 'Calcular ahorro'}
                    </Button>
                  </div>
                  <p className="pp-help">Mide cuánto pesaría cada foto reprocesada a JPG (máximo 1600×2000). No sube ni cambia nada: es solo la vista previa.</p>
                  {prevEstado !== 'idle' && (
                    <div className="pp-opt-preview">
                      {prevResumen && (
                        <p className="pp-opt-resumen">
                          {prevResumen.aReprocesar > 0
                            ? <>Conviene optimizar <strong>{prevResumen.aReprocesar}</strong> de {prevResumen.medidas} foto(s): ahorro estimado <strong>{pesoLegible(prevResumen.ahorroTotalBytes)}</strong>.</>
                            : <>Ninguna foto cumple el criterio: ya están optimizadas.</>}
                          {prevResumen.fallidas > 0 && <> {prevResumen.fallidas} no se pudieron leer.</>}
                        </p>
                      )}
                      <ul className="pp-opt-lista">
                        {prevResultados.map((r, i) => {
                          const otros = (prevUsos[r.url] || []).filter((p) => Number(p.id) !== Number(productId));
                          const est = optEstados[r.url]; // estado de ESCRITURA por foto (cola/optimizando/lista/error)
                          return (
                            <li key={r.url || i} className={`pp-opt-item${r.ok && r.reprocesar ? ' pp-opt-item--si' : ''}`}>
                              <span className="pp-opt-donde">{(r.usos || []).join(', ') || 'Foto'}</span>
                              {r.ok ? (
                                <span className="pp-opt-nums">
                                  {r.ancho}×{r.alto} · {pesoLegible(r.pesoOriginal)} → {pesoLegible(r.pesoNuevo)} · {r.reprocesar
                                    ? <strong>{r.motivo === 'sobredimensionada' ? 'muy grande' : `−${Math.round(r.ahorroPct * 100)}%`}</strong>
                                    : etiquetaFilaSinReproceso(optimizadaPorId[r.id])}
                                </span>
                              ) : (
                                <span className="pp-opt-nums pp-warn">no se pudo leer ({r.error})</span>
                              )}
                              {r.ok && r.ancho && r.alto && esBajaResolucion(r.ancho, r.alto, propFoto.ratioW, propFoto.ratioH) && (
                                <small className="pp-opt-baja">{r.ancho}×{r.alto} · baja resolución: el tema no podrá servirla nítida en escritorio; re-exportar desde el original si existe</small>
                              )}
                              {/* P3-9b: avance de ESCRITURA de esta foto. */}
                              <EstadoOptimizacionFoto est={est} onReintentar={() => reintentarOptimizar(r)} disabled={optimizando} />
                              {otros.length > 0 && (
                                <small className="pp-opt-reuso">También en: {otros.map((p) => p.name).filter(Boolean).join(', ')} — al optimizar se cambia en todos</small>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                      {/* P3-9b (ESCRITURA): optimizar de verdad las candidatas. Sube el
                          JPG nuevo, reapunta todas las apariciones y conserva la vieja. */}
                      {prevEstado === 'listo' && candidatasOptimizar.length > 0 && (
                        <div className="pp-opt-aplicar">
                          <Button size="sm" variant="primary" onClick={optimizarFotos} disabled={optimizando}
                            title="Sube las versiones optimizadas y las reapunta en el producto. La original queda guardada por si hay que revertir.">
                            {optimizando
                              ? `Optimizando ${optProgreso.hecho}/${optProgreso.total}${optProgreso.actual ? ` · ${optProgreso.actual}` : ''}…`
                              : `Optimizar ${candidatasOptimizar.length} foto${candidatasOptimizar.length === 1 ? '' : 's'}`}
                          </Button>
                          <small className="pp-help">La foto original queda guardada por si hay que revertir; se borra solo con una confirmación aparte.</small>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              <div className="pp-subhead">Vitrina</div>
              <ComingSection title="Vitrina" nota="Atributos estructurados, detalles y cuidados, cómo usarlo y tabla de medidas llegan cuando el tema Vitrina esté activo (sección secundaria; no cuenta en el estado)." />
            </div>
          )}
        </div>

        {/* PARTE A — columna derecha fija */}
        <aside className="pp-aside">
          <div className="pp-aside-card">
            {form.images[0] ? (
              <>
                <div className="pp-aside-main">
                  <img src={form.images[0].src} alt={form.images[0].alt || 'Imagen principal'} />
                </div>
                <Button size="sm" onClick={() => setImgSel({ target: 'main' })}>Cambiar</Button>
              </>
            ) : (
              <button type="button" className="pp-aside-empty" onClick={() => setImgSel({ target: 'main' })}>
                Agrega la imagen principal
              </button>
            )}
            <button type="button" className="pp-aside-gallery" onClick={() => setTab('otras')}>
              Galería: {Math.max(0, form.images.length - (form.images.length ? 1 : 0))} foto{form.images.length - 1 === 1 ? '' : 's'}
            </button>
          </div>
          <div className="pp-aside-card pp-aside-estado">
            <div className="pp-subhead">Estado de la ficha</div>
            {/* Tanda 6 (PARTE B): el evaluador en vivo, sin red. */}
            <FichaEstadoPanel resultado={resultadoFicha} onIr={irARegla} />
          </div>
        </aside>
        </div>
        {/* Tanda 4b (ítem 3): la tabla de variaciones es una tarjeta propia de
            ancho completo, debajo del layout de dos columnas. */}
        {/* P3-5b (remate): un comestible no usa el bloque genérico de variaciones —
            sus variaciones son sabor × pack en la sección Comestible. */}
        {tab === 'general' && esConsumible && (
          <div className="pp-section pp-section--vars">
            <button type="button" className="pp-link-comestible" onClick={() => setTab('comestible')}>
              Las variaciones de un comestible se definen en la sección Comestible (sabores × packs)
            </button>
          </div>
        )}
        {tab === 'general' && !esConsumible && esVariable && variacionesTabla}
        </>
      )}

      {confirmDlg && (
        <DialogoDecision abierto onClose={() => setConfirmDlg(null)} ariaLabel={confirmDlg.title}>
          <h3>{confirmDlg.title}</h3>
          <p>{confirmDlg.message}</p>
          <div className="pp-confirm-actions">
            <Button onClick={() => setConfirmDlg(null)}>Volver</Button>
            <Button variant="primary" onClick={confirmDlg.onYes}>{confirmDlg.confirmLabel}</Button>
          </div>
        </DialogoDecision>
      )}

      {/* P3-10 punto 1: conflicto de edición — el producto cambió en la tienda desde
          que se abrió la ficha. Se muestra qué cambió (campo por campo) y se deja
          resolver: recargar (y conservar lo que estabas editando) o sobrescribir. */}
      {conflicto && (
        <DialogoDecision abierto onClose={() => setConflicto(null)} ariaLabel="El producto cambió en la tienda">
          <h3>El producto cambió en la tienda</h3>
          <p className="pp-conflicto-sub">
            Alguien lo editó directamente en la tienda desde que abriste esta ficha. Para no pisar ese cambio sin querer, no se guardó nada.
          </p>
          {conflicto.cambios && conflicto.cambios.length > 0 && (
            <ul className="pp-conflicto-lista">
              {conflicto.cambios.map((c) => (
                <li key={c.campo}>
                  <strong>{etiquetaCampoConflicto(c.campo)}:</strong> en la tienda ahora es «{valorConflicto(c.campo, c.en_la_tienda)}»
                  {c.intentado != null && c.intentado !== '' ? ` (ibas a guardar «${valorConflicto(c.campo, c.intentado)}»)` : ''}
                </li>
              ))}
            </ul>
          )}
          <div className="pp-confirm-actions">
            <Button onClick={resolverConflictoRecargar}>Recargar desde la tienda</Button>
            <Button variant="primary" onClick={resolverConflictoSobrescribir}>Sobrescribir con lo mío</Button>
          </div>
        </DialogoDecision>
      )}

      {/* Tanda 7 (PARTE B): diálogo propio de publicar-incompleta. El primario
          lleva a COMPLETAR (navega a la primera pendiente); publicar es la
          acción secundaria. Solo aparece sin obligatorias pendientes. */}
      {publicarDlg && (
        <DialogoDecision abierto onClose={() => setPublicarDlg(null)} className="pp-publicar"
          ariaLabel={`Publicar con ${publicarDlg.pendientes.length} ${publicarDlg.pendientes.length === 1 ? 'recomendación pendiente' : 'recomendaciones pendientes'}`}>
          <h3>Publicar con {publicarDlg.pendientes.length} {publicarDlg.pendientes.length === 1 ? 'recomendación pendiente' : 'recomendaciones pendientes'}</h3>
          <p className="pp-publicar-sub">La ficha se puede publicar, pero Google y las clientas la verán incompleta.</p>
          <ul className="pp-publicar-lista">
            {publicarDlg.pendientes.map((r) => (
              <li key={r.id}>
                <CircleDashed size={15} aria-hidden="true" />
                <span className="pp-publicar-txt">
                  <strong>{r.nombre}</strong>
                  {(r.detalle || r.mensaje) && <span className="pp-publicar-det">{r.detalle || r.mensaje}</span>}
                </span>
              </li>
            ))}
          </ul>
          <div className="pp-confirm-actions">
            <Button onClick={() => { setPublicarDlg(null); doWrite('publish'); }}>Publicar igual</Button>
            <Button variant="primary" onClick={() => {
              const primera = publicarDlg.pendientes[0];
              setPublicarDlg(null);
              if (primera) irARegla(primera.destino);
            }}>Completar la ficha</Button>
          </div>
        </DialogoDecision>
      )}

      {/* Tanda 7b: alta de variación. La primera vez pide el eje; el precio
          por defecto es el de la última fila (o el del simple al convertirlo,
          igual que el stock — así los valores del simple no se pierden). */}
      {altaVar && (
        <AgregarVariacionModal abierto onClose={() => setAltaVar(false)} onCrear={crearVariacion}
          ejeExistente={!multiEje && ejeExistente ? ejeNorm.nombre : null} ejesCanonicos={listaEjes} skuPadre={form.sku}
          ejesMulti={multiEje ? nombresEjes : null}
          combinacionExiste={multiEje ? (valores) => vRows.some((r) => (r.opciones || []).every((o) => String(o.option) === String(valores[o.name]))) : null}
          precioPorDefecto={vRows.length ? vRows[vRows.length - 1].regularPrice : form.regularPrice}
          stockPorDefecto={vRows.length === 0 ? form.stockQty : ''}
          esPrimera={vRows.length === 0} nombreProducto={form.name}
          fotosDelProducto={fotosDelProductoParaSelector(form.images, variations)} />
      )}

      <ProductImageSelector
        open={!!imgSel}
        onClose={() => setImgSel(null)}
        onSelect={onSeleccionImagen}
        nombre={form.name}
        // P3-1 (punto 5): para una VARIACIÓN, la primera pestaña son las fotos
        // que el producto ya tiene (se asocia por id, sin re-subir).
        fotosDelProducto={imgSel?.target === 'variation' ? fotosDelProductoParaSelector(form.images, variations) : null}
        contexto={{ productoId: productId ?? wooId, variacionId: imgSel?.variationId ?? null }}
        variante={imgSel?.variationId ? varLabel(variations.find((v) => v.id === imgSel.variationId) || {}) : ''}
        proporcion={propFoto}
      />
    </div>
  );
}
